import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDashboardSection } from '../hooks/useDashboardSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar, faBox, faShoppingCart, faDollarSign, faChartLine, faCog,
  faBars, faMoon, faSun, faPlus, faTruck, faChartPie, faMobileAlt,
  faCheckCircle, faNairaSign, faEdit, faEye, faTrash, faFilter,
  faSearch, faUpload, faSave, faUser, faEnvelope, faPhone, faMapMarkerAlt,
  faTimes, faCheck, faClipboardList, faWallet, faComments, faFileAlt,
  faCrown, faHeadset, faPaperPlane, faStar, faAward, faBolt, faShieldAlt, faGift,
  faBell, faSignOutAlt, faUserCircle, faQuestionCircle, faHome, faHistory,
  faList, faThLarge, faHeadphones, faSpinner, faStore, faExchangeAlt, faLock, faUniversity, faTrophy, faClock,
  faExclamationTriangle, faBan, faTrashAlt, faDownload
} from '@fortawesome/free-solid-svg-icons';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler,
} from 'chart.js';
import { format, startOfWeek, endOfWeek, subDays } from 'date-fns';
import lightLogo from '../assets/autoboy_logo2.png';
import darkLogo from '../assets/autoboy_logo3.png';
import { sellerAPI, notificationsAPI, walletAPI, userAPI, categoriesAPI, supportAPI, badgesAPI, paymentAPI, productsAPI, swapAPI } from '../services/api';
import { getStoredDarkMode, setStoredDarkMode } from '../utils/theme';
import { uploadMultipleImages, validateImageFile, uploadImage } from '../services/imageUploadService';
import { useNotifications } from '../context/NotificationContext';
import { TableCard, DataTable, THead, Th, Tr, Td, Badge, TableEmpty, IconBtn, statusTone } from './ui/DataTable';
import OrderIMEIButton from './OrderIMEIButton';
import ColorMultiSelect from './ColorMultiSelect';
import IMEISubmissionModal from './IMEISubmissionModal';
import KYCVerification from './KYCVerification';
import KYCStatusBanner from './KYCStatusBanner';
import BankAccountsManager from './BankAccountsManager';
import EscrowAnalytics from './EscrowAnalytics';
import EscrowSettingsPanel from './EscrowSettingsPanel';
import AddressManagement from './AddressManagement';
import TwoFactorAuth from './TwoFactorAuth';
import Disputes from './Disputes';
import SwapDeals from './SwapDeals';
import FlashDealsStrip from './FlashDealsStrip';
import { useSwap } from '../context/SwapContext';
import './PremiumSellerDashboard.css';
import '../assets/css/settings-modern.css';
import './PremiumSellerDashboard_orders.css';
import '../assets/css/modern-products.css';
import '../assets/css/wallet-modals.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler
);

// Helper functions for badge system
const calculateSellerLevel = (points) => {
  if (points >= 10000) return 'Diamond';
  if (points >= 5000) return 'Gold';
  if (points >= 2000) return 'Silver';
  if (points >= 500) return 'Bronze';
  return 'Beginner';
};

const getNextLevelInfo = (currentPoints) => {
  if (currentPoints >= 10000) {
    return { nextLevel: 'Diamond Max', nextLevelPoints: 10000 };
  }
  if (currentPoints >= 5000) {
    return { nextLevel: 'Diamond', nextLevelPoints: 10000 };
  }
  if (currentPoints >= 2000) {
    return { nextLevel: 'Gold', nextLevelPoints: 5000 };
  }
  if (currentPoints >= 500) {
    return { nextLevel: 'Silver', nextLevelPoints: 2000 };
  }
  return { nextLevel: 'Bronze', nextLevelPoints: 500 };
};

// Fee perks per badge level used to claim specific discounted platform fees
// (e.g. "Diamond: 2%") that don't correspond to any real mechanism —
// commission is entirely price-tier based (models.StaticCommissionTiers:
// 30%/20%/8%/7%/6%/4% by item price), identical for every seller regardless
// of badge level. A Bronze-level premium seller pays the exact same rate as
// a Diamond one for the same-priced item. Removed rather than inventing a
// level-based discount system that doesn't exist server-side.
const getSellerLevelPerks = (level) => {
  const perksByLevel = {
    'Diamond': [
      { icon: '💎', title: 'Diamond Seller Badge', description: 'Exclusive diamond seller badge' },
      { icon: '⚡', title: 'Priority Support', description: '24/7 dedicated seller support' },
      { icon: '🚀', title: 'Featured Listings', description: 'Products featured in search results' },
      { icon: '📊', title: 'Advanced Analytics', description: 'Detailed sales insights and reports' }
    ],
    'Gold': [
      { icon: '🥇', title: 'Gold Seller Badge', description: 'Prestigious gold seller status' },
      { icon: '⚡', title: 'Priority Support', description: 'Priority customer support access' },
      { icon: '🎯', title: 'Promoted Listings', description: 'Boost your products visibility' },
      { icon: '📈', title: 'Sales Analytics', description: 'Track your sales performance' }
    ],
    'Silver': [
      { icon: '🥈', title: 'Silver Seller Badge', description: 'Silver tier seller recognition' },
      { icon: '💬', title: 'Enhanced Support', description: 'Faster response times' },
      { icon: '📊', title: 'Basic Analytics', description: 'View sales statistics' }
    ],
    'Bronze': [
      { icon: '🥉', title: 'Bronze Seller Badge', description: 'Bronze tier seller status' },
      { icon: '📧', title: 'Email Support', description: 'Email support access' }
    ],
    'Beginner': [
      { icon: '🌟', title: 'New Seller', description: 'Welcome to AutoboyExpress!' },
      { icon: '📚', title: 'Seller Resources', description: 'Access to seller guides' }
    ]
  };
  return perksByLevel[level] || perksByLevel['Beginner'];
};

const PremiumSellerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications, unreadCount, markAsRead, showCustomToast } = useNotifications();
  const {
    swaps, loading: swapLoading, swapMetadata,
    getReceivedSwaps, respondToOffer, declineOffer, calculateSwapFees, fetchSwaps,
    confirmShipment, confirmDelivery,
  } = useSwap();
  const [swapRequestsTab, setSwapRequestsTab] = useState('p2p');
  const [swapTopupAmounts, setSwapTopupAmounts] = useState({});
  const [swapActionLoading, setSwapActionLoading] = useState(null);
  // Swap whose seller-side IMEI prompt is open — see SellerDashboard for the
  // same flow. Collected once on accept, never shown back, never seen by the buyer.
  const [swapIMEITarget, setSwapIMEITarget] = useState(null);
  const [swapIMEISubmittedIds, setSwapIMEISubmittedIds] = useState([]);
  // Section lives in the URL (?section=...) so Back returns to the tab
  // you were on — see useDashboardSection.
  const [activeSection, setActiveSection] = useDashboardSection('dashboard');
  const [isDarkMode, setIsDarkMode] = useState(() => getStoredDarkMode());
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const [showViewProductModal, setShowViewProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [productsViewMode, setProductsViewMode] = useState('grid'); // 'grid' or 'list'
  const [stats, setStats] = useState({
    activeProducts: 24,
    pendingOrders: 5,
    totalSales: 152,
    totalEarnings: 2500000
  });
  const [newProduct, setNewProduct] = useState({
    title: '',
    category: '',
    brand: '',
    model: '',
    color: '',
    colors: [],
    condition: 'new',
    price: '',           // Regular price (shown to buyers)
    sellerPrice: '',     // Wholesale price for sellers (optional)
    description: '',
    specifications: '',
    quantity: '',
    location: '',
    swapAvailable: false,
    tags: ''
  });

  // Device color options for whichever product form (create or edit) is
  // currently open — colors are model-specific, never a generic list shared
  // across every listing. Shared state is safe since only one of the two
  // modals is ever open at a time.
  const [availableColors, setAvailableColors] = useState([]);
  const [loadingColors, setLoadingColors] = useState(false);
  const activeProductModel = showEditProductModal ? editProduct?.model : newProduct.model;
  useEffect(() => {
    const model = activeProductModel?.trim();
    if (!model) {
      setAvailableColors([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setLoadingColors(true);
      try {
        const res = await productsAPI.getDeviceColors(model);
        const colors = res?.data?.colors || res?.colors || [];
        setAvailableColors(Array.isArray(colors) ? colors : []);
      } catch {
        setAvailableColors([]);
      } finally {
        setLoadingColors(false);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [activeProductModel]);

  // Products and orders state
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // User profile data
  const [userProfile, setUserProfile] = useState({
    name: 'Loading...',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    avatar: '',
    businessName: '',
    businessAddress: '',
    businessPhone: '',
    bio: '',
    userType: 'seller',
    isVerified: false
  });

  // Reports and analytics data - dynamic from backend
  const [reportsData, setReportsData] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    conversionRate: 0,
    revenueGrowth: 0,
    ordersGrowth: 0,
    avgOrderGrowth: 0,
    conversionGrowth: 0,
    topProducts: []
  });
  const [enhancedStats, setEnhancedStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    conversionRate: 0,
    returnRate: 0,
    customerSatisfaction: 4.5,
    revenueGrowth: 0,
    ordersGrowth: 0,
    avgOrderGrowth: 0,
    conversionGrowth: 0,
    topProducts: []
  });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [walletData, setWalletData] = useState(null);

  // Enhanced Wallet state variables
  const [walletBalance, setWalletBalance] = useState({
    balance: 0,
    heldBalance: 0
  });
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [isLoadingWallet, setIsLoadingWallet] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showBankAccounts, setShowBankAccounts] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [isProcessingTopup, setIsProcessingTopup] = useState(false);
  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: '',
    bankAccount: '',
    bankName: '',
    accountName: '',
    selectedBankId: '' // For verified bank accounts
  });
  const [isProcessingWithdrawal, setIsProcessingWithdrawal] = useState(false);
  const [userBankAccounts, setUserBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);

  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    orderUpdates: true,
    priceAlerts: true,
    promotions: false,
    newMessages: true,
  });
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);

  // Active sessions
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [terminatingSession, setTerminatingSession] = useState(null);

  // Avatar/Profile Picture state
  const [avatarUploadMethod, setAvatarUploadMethod] = useState('url');
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarFileInputRef = React.useRef(null);

  // Settings tab + loading
  const [settingsTab, setSettingsTab] = useState('profile');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [passwordForDeletion, setPasswordForDeletion] = useState('');
  const [exportingData, setExportingData] = useState(false);

  const [settingsFormData, setSettingsFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    businessName: '',
    businessAddress: '',
    businessPhone: '',
    bio: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // VIP Support state
  const [vipTicketForm, setVipTicketForm] = useState({
    subject: '',
    priority: 'medium',
    message: ''
  });
  const [isSubmittingVipTicket, setIsSubmittingVipTicket] = useState(false);

  // Orders Pagination and Filter state
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [ordersFilter, setOrdersFilter] = useState('all'); // 'all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'
  const [ordersSearchQuery, setOrdersSearchQuery] = useState('');

  // Badge/Achievement data - dynamic from backend
  const [badgeProgress, setBadgeProgress] = useState({
    currentLevel: 'Beginner',
    currentPoints: 0,
    nextLevel: 'Bronze',
    nextLevelPoints: 500,
    achievements: [],
    perks: []
  });

  // Chart data - will be populated from backend
  const [salesChartData, setSalesChartData] = useState({
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{
      label: 'Sales (₦)',
      data: [0, 0, 0, 0, 0, 0, 0],
      borderColor: '#22C55E',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      tension: 0.4,
      fill: true,
    }]
  });

  const [categoryChartData, setCategoryChartData] = useState({
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: ['#22C55E', '#16A34A', '#4ADE80', '#10B981', '#6B7280'],
      borderWidth: 0,
    }]
  });

  const [monthlyEarningsData, setMonthlyEarningsData] = useState({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Earnings (₦)',
      data: [0, 0, 0, 0, 0, 0],
      backgroundColor: '#22C55E',
      borderRadius: 8,
    }]
  });

  // Helper function to get clean display name (prevents duplicates)
  const getDisplayName = (nameString) => {
    if (!nameString) return 'User';

    // Remove extra spaces and trim
    const cleanName = String(nameString).trim().replace(/\s+/g, ' ');

    // Check if name is duplicated (e.g., "John John" or "cybersage cybersage")
    const words = cleanName.split(' ');
    if (words.length >= 2) {
      // Check if consecutive words are the same
      const uniqueWords = [];
      for (let i = 0; i < words.length; i++) {
        if (i === 0 || words[i].toLowerCase() !== words[i - 1].toLowerCase()) {
          uniqueWords.push(words[i]);
        }
      }
      return uniqueWords.join(' ');
    }

    return cleanName;
  };

  useEffect(() => {
    document.body.classList.toggle('AutoboyExpress-dash-dark-mode', isDarkMode);
    // Tailwind's compiled `:where(...)` selector for our custom darkMode config
    // only expands `.tw-dark-mode` to also match descendants (`.tw-dark-mode *`);
    // `.tw-AutoboyExpress-dash-dark-mode` compiles as a self-match-only term, so
    // toggling it on <body> never reaches nested `dark:` utilities (see ui/DataTable.jsx).
    // `tw-dark-mode` is the class that actually cascades — ThemeSync strips it on
    // dashboard routes to avoid storefront bleed, so we re-assert it here.
    document.body.classList.toggle('tw-dark-mode', isDarkMode);
    setStoredDarkMode(isDarkMode);
  }, [isDarkMode]);

  // Sync avatar preview with userProfile
  useEffect(() => {
    if (userProfile?.avatar) {
      setAvatarPreview(userProfile.avatar);
    }
  }, [userProfile]);

  // Load notification preferences from API
  useEffect(() => {
    const loadNotifPrefs = async () => {
      try {
        const res = await notificationsAPI.getPreferences();
        const data = res?.data || {};
        setNotificationPrefs({
          emailNotifications: data.email_notifications ?? true,
          pushNotifications: data.push_notifications ?? true,
          smsNotifications: data.sms_notifications ?? false,
          orderUpdates: data.order_updates ?? true,
          priceAlerts: data.price_alerts ?? true,
          promotions: data.promotions ?? false,
          newMessages: data.new_messages ?? true,
        });
      } catch {
        // keep defaults on error
      }
    };
    loadNotifPrefs();
  }, []);

  const handleSaveNotifPrefs = async () => {
    try {
      setSavingNotifPrefs(true);
      await notificationsAPI.updatePreferences({
        email_notifications: notificationPrefs.emailNotifications,
        push_notifications: notificationPrefs.pushNotifications,
        sms_notifications: notificationPrefs.smsNotifications,
        order_updates: notificationPrefs.orderUpdates,
        price_alerts: notificationPrefs.priceAlerts,
        promotions: notificationPrefs.promotions,
        new_messages: notificationPrefs.newMessages,
      });
      showCustomToast({ type: 'success', message: 'Notification preferences saved.' });
    } catch {
      showCustomToast({ type: 'error', message: 'Failed to save notification preferences.' });
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  // Refresh swap data when swap-deals section becomes active
  useEffect(() => {
    if (activeSection === 'swap-deals') {
      fetchSwaps();
    }
  }, [activeSection]);

  // Load sessions when settings section is active
  useEffect(() => {
    if (activeSection === 'settings') {
      setSessionsLoading(true);
      userAPI.getActiveSessions()
        .then(res => setActiveSessions(Array.isArray(res?.data?.sessions || res?.data) ? (res?.data?.sessions || res?.data) : []))
        .catch(() => setActiveSessions([]))
        .finally(() => setSessionsLoading(false));
    }
  }, [activeSection]);

  const handleTerminateSession = async (sessionId) => {
    try {
      setTerminatingSession(sessionId);
      await userAPI.terminateSession(sessionId);
      setActiveSessions(prev => prev.filter(s => (s.id || s._id) !== sessionId));
      showCustomToast({ type: 'success', message: 'Session terminated.' });
    } catch { showCustomToast({ type: 'error', message: 'Failed to terminate session.' }); }
    finally { setTerminatingSession(null); }
  };

  const handleTerminateAllSessions = async () => {
    if (!window.confirm('Sign out of all other devices?')) return;
    try {
      await userAPI.terminateAllOtherSessions();
      const res = await userAPI.getActiveSessions();
      setActiveSessions(Array.isArray(res?.data?.sessions || res?.data) ? (res?.data?.sessions || res?.data) : []);
      showCustomToast({ type: 'success', message: 'All other sessions terminated.' });
    } catch { showCustomToast({ type: 'error', message: 'Failed to terminate sessions.' }); }
  };

  // Order action state
  const [processingOrderAction, setProcessingOrderAction] = useState(null);

  const handleShipOrder = async (orderId) => {
    const trackingNumber = window.prompt('Enter tracking number (optional):') ?? '';
    setProcessingOrderAction(`${orderId}-ship`);
    try {
      await sellerAPI.shipOrder(orderId, { tracking_number: trackingNumber });
      setOrders(prev => prev.map(o => (o.id === orderId || o._id === orderId) ? { ...o, status: 'shipped' } : o));
      showCustomToast({ type: 'success', message: 'Order marked as shipped' });
    } catch {
      showCustomToast({ type: 'error', message: 'Failed to update order status' });
    } finally {
      setProcessingOrderAction(null);
    }
  };

  const handleMarkDelivered = async (orderId) => {
    if (!window.confirm('Mark this order as delivered?')) return;
    setProcessingOrderAction(`${orderId}-deliver`);
    try {
      await sellerAPI.markOrderDelivered(orderId);
      setOrders(prev => prev.map(o => (o.id === orderId || o._id === orderId) ? { ...o, status: 'delivered' } : o));
      showCustomToast({ type: 'success', message: 'Order marked as delivered' });
    } catch {
      showCustomToast({ type: 'error', message: 'Failed to update order status' });
    } finally {
      setProcessingOrderAction(null);
    }
  };

  const handleRejectOrder = async (orderId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    setProcessingOrderAction(`${orderId}-reject`);
    try {
      await sellerAPI.rejectOrder(orderId, reason);
      setOrders(prev => prev.map(o => (o.id === orderId || o._id === orderId) ? { ...o, status: 'cancelled' } : o));
      showCustomToast({ type: 'success', message: 'Order rejected' });
    } catch {
      showCustomToast({ type: 'error', message: 'Failed to reject order' });
    } finally {
      setProcessingOrderAction(null);
    }
  };

  // Sync settings form data with userProfile
  useEffect(() => {
    setSettingsFormData({
      firstName: userProfile.firstName || '',
      lastName: userProfile.lastName || '',
      phone: userProfile.phone || '',
      email: userProfile.email || '',
      businessName: userProfile.businessName || '',
      businessAddress: userProfile.businessAddress || '',
      businessPhone: userProfile.businessPhone || '',
      bio: userProfile.bio || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  }, [userProfile]);

  // Fetch wallet balance and transactions when wallet or earnings section is active
  useEffect(() => {
    if (activeSection === 'wallet' || activeSection === 'earnings') {
      fetchWalletBalance();
      fetchWalletTransactions();
    }
  }, [activeSection]);

  // Fetch bank accounts when withdrawal modal opens
  useEffect(() => {
    if (showWithdrawModal) {
      fetchUserBankAccounts();
    }
  }, [showWithdrawModal]);

  // Auto-refresh wallet balance every 30 seconds when on wallet/earnings section
  useEffect(() => {
    let refreshInterval;
    if (activeSection === 'wallet' || activeSection === 'earnings') {
      refreshInterval = setInterval(() => {
        fetchWalletBalance();
        fetchWalletTransactions();
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [activeSection]);

  // Auto-refresh analytics data every 60 seconds when on analytics section
  useEffect(() => {
    let analyticsRefreshInterval;
    if (activeSection === 'analytics') {
      analyticsRefreshInterval = setInterval(async () => {
        try {
          const [salesAnalytics, revenueAnalytics, productAnalytics] = await Promise.all([
            sellerAPI.getSalesAnalytics().catch(() => ({ data: null })),
            sellerAPI.getRevenueAnalytics().catch(() => ({ data: null })),
            sellerAPI.getProductAnalytics().catch(() => ({ data: null }))
          ]);

          // Update analytics data
          if (salesAnalytics?.data) {
            setAnalyticsData(salesAnalytics.data);

            // Update charts from analytics data
            if (salesAnalytics.data.daily_sales && Array.isArray(salesAnalytics.data.daily_sales)) {
              setSalesChartData({
                labels: salesAnalytics.data.daily_sales.map(d => d.day || d.label || d.date),
                datasets: [{
                  label: 'Daily Sales (₦)',
                  data: salesAnalytics.data.daily_sales.map(d => parseFloat(d.amount || d.total || 0)),
                  borderColor: '#22C55E',
                  backgroundColor: 'rgba(34, 197, 94, 0.1)',
                  tension: 0.4,
                  fill: true,
                }]
              });
            }

            if (salesAnalytics.data.category_breakdown && Array.isArray(salesAnalytics.data.category_breakdown)) {
              const categories = salesAnalytics.data.category_breakdown;
              setCategoryChartData({
                labels: categories.map(c => c.category || c.name),
                datasets: [{
                  data: categories.map(c => parseFloat(c.amount || c.total || c.count || 0)),
                  backgroundColor: ['#22C55E', '#16A34A', '#4ADE80', '#10B981', '#6B7280', '#059669'],
                  borderWidth: 0,
                }]
              });
            }
          }

          if (revenueAnalytics?.data && revenueAnalytics.data.monthly_revenue) {
            setMonthlyEarningsData({
              labels: revenueAnalytics.data.monthly_revenue.map(m => m.month || m.label),
              datasets: [{
                label: 'Monthly Earnings (₦)',
                data: revenueAnalytics.data.monthly_revenue.map(m => parseFloat(m.amount || m.revenue || m.total || 0)),
                backgroundColor: '#22C55E',
                borderRadius: 8,
              }]
            });
          }

          // Update enhanced stats from product analytics. revenue_growth/
          // orders_growth/avg_order_growth/conversion_growth never existed on
          // this (or any) response — the backend computes exactly one real
          // growth figure, GetSellerSalesAnalytics's growth_rate (this
          // month's revenue vs last month's), which is what revenueGrowth
          // now reads instead. There's no real backend computation for the
          // other three, so they're left null rather than shown as a
          // permanent, meaningless "+0.0%".
          if (productAnalytics?.data) {
            setEnhancedStats(prev => ({
              ...prev,
              topProducts: productAnalytics.data.top_products || prev.topProducts
            }));
          }
          if (salesAnalytics?.data && salesAnalytics.data.growth_rate != null) {
            setEnhancedStats(prev => ({ ...prev, revenueGrowth: salesAnalytics.data.growth_rate }));
          }

        } catch (error) {
        }
      }, 60000); // Refresh every 60 seconds
    }

    return () => {
      if (analyticsRefreshInterval) {
        clearInterval(analyticsRefreshInterval);
      }
    };
  }, [activeSection]);

  // Handle navigation from ProductDetails (e.g., quick-link deep links into a specific section)
  useEffect(() => {
    if (location.state?.activeSection) {
      setActiveSection(location.state.activeSection);

      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Check for payment success on page load - Verify payment then update wallet
  useEffect(() => {
    const verifyAndUpdateWallet = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      const reference = urlParams.get('reference') ||
                       urlParams.get('trxref') ||
                       localStorage.getItem('pending_payment_reference');

      if (paymentStatus === 'success' || reference) {
        try {
          // Switch to wallet section
          setActiveSection('wallet');

          // Clean up URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);

          // Verify payment with backend
          const { paymentAPI } = await import('../services/api');

          const verificationResponse = await paymentAPI.verifyPayment(reference);

          if (verificationResponse && verificationResponse.success) {
            const paymentData = verificationResponse.data;

            // Check if payment is for wallet top-up
            if (paymentData.payment_type === 'wallet_topup') {
              // Fetch updated wallet balance
              const walletResponse = await walletAPI.getBalance();

              const verifyWalPayload = walletResponse?.data || walletResponse;
              if (verifyWalPayload) {
                const balanceData = verifyWalPayload.balance !== undefined ? verifyWalPayload : (verifyWalPayload.data || verifyWalPayload);
                const newBalance = balanceData.balance || 0;

                setWalletBalance({
                  balance: newBalance,
                  heldBalance: balanceData.held_balance || balanceData.heldBalance || 0
                });


                // Show credit notification with amount
                const topupAmount = parseFloat(paymentData.amount) || 0;
                showCustomToast({
                  type: 'success',
                  title: 'Wallet Credited',
                  message: `+₦${topupAmount.toLocaleString()} | New Balance: ₦${newBalance.toLocaleString()}`
                });
              }

              // Clear pending payment data
              localStorage.removeItem('pending_payment_reference');
              localStorage.removeItem('pending_payment_id');

              // Refresh transactions
              fetchWalletTransactions();

              // Switch to wallet section to show updated balance
              setActiveSection('wallet');
            } else {
              showCustomToast({
                type: 'success',
                title: 'Payment Verified',
                message: 'Payment verified successfully!'
              });
            }
          } else {
            showCustomToast({
              type: 'error',
              title: 'Verification Failed',
              message: verificationResponse?.message || 'Payment verification failed. Please contact support.'
            });
          }
        } catch (error) {
          showCustomToast({
            type: 'error',
            title: 'Verification Error',
            message: error.message || 'Error verifying payment. Please contact support.'
          });
        }
      }
    };

    verifyAndUpdateWallet();
  }, []);

  // Helper functions to generate chart data from orders when backend analytics not available
  const generateSalesChartFromOrders = (orders) => {
    if (!orders || orders.length === 0) {
      return;
    }

    // Get last 7 days
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const last7Days = [];
    const salesByDay = {};

    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayName = days[date.getDay()];
      last7Days.push(dayName);
      salesByDay[dayName] = 0;
    }

    // Calculate sales for each day
    orders.forEach(order => {
      if (order.status === 'completed' || order.status === 'delivered') {
        const orderDate = new Date(order.date);
        const dayName = days[orderDate.getDay()];
        if (salesByDay.hasOwnProperty(dayName)) {
          salesByDay[dayName] += order.amount || 0;
        }
      }
    });


    setSalesChartData({
      labels: last7Days,
      datasets: [{
        label: 'Daily Sales (₦)',
        data: last7Days.map(day => salesByDay[day]),
        borderColor: '#22C55E',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.4,
        fill: true,
      }]
    });
  };

  const generateCategoryChartFromProducts = (products) => {
    if (!products || products.length === 0) {
      return;
    }

    // Group products by category
    const categoryCount = {};
    products.forEach(product => {
      const category = product.category || 'Uncategorized';
      categoryCount[category] = (categoryCount[category] || 0) + 1;
    });

    // Get top 5 categories
    const sortedCategories = Object.entries(categoryCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);


    setCategoryChartData({
      labels: sortedCategories.map(([category]) => category),
      datasets: [{
        data: sortedCategories.map(([, count]) => count),
        backgroundColor: ['#22C55E', '#16A34A', '#4ADE80', '#10B981', '#6B7280'],
        borderWidth: 0,
      }]
    });
  };

  const generateMonthlyEarningsFromOrders = (orders) => {
    if (!orders || orders.length === 0) {
      return;
    }

    // Get last 6 months
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const last6Months = [];
    const earningsByMonth = {};

    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthName = months[date.getMonth()];
      last6Months.push(monthName);
      earningsByMonth[monthName] = 0;
    }

    // Calculate earnings for each month
    orders.forEach(order => {
      if (order.status === 'completed' || order.status === 'delivered') {
        const orderDate = new Date(order.date);
        const monthName = months[orderDate.getMonth()];
        if (earningsByMonth.hasOwnProperty(monthName)) {
          earningsByMonth[monthName] += order.amount || 0;
        }
      }
    });


    setMonthlyEarningsData({
      labels: last6Months,
      datasets: [{
        label: 'Monthly Earnings (₦)',
        data: last6Months.map(month => earningsByMonth[month]),
        backgroundColor: '#22C55E',
        borderRadius: 8,
      }]
    });
  };

  // Fetch all dashboard data from backend
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const [
          userProfileData,
          dashboardData,
          productsData,
          ordersData,
          walletResponse,
          salesAnalytics,
          revenueAnalytics,
          productAnalytics,
          categoriesData,
          userBadgesData,
          userRewardsData
        ] = await Promise.all([
          userAPI.getProfile().catch(() => ({ data: null })),
          sellerAPI.getDashboard().catch(() => ({ data: null })),
          sellerAPI.getProducts().catch(() => ({ data: [] })),
          sellerAPI.getOrders().catch(() => ({ data: [] })),
          walletAPI.getBalance().catch(() => ({ data: null })),
          sellerAPI.getSalesAnalytics().catch(() => ({ data: null })),
          sellerAPI.getRevenueAnalytics().catch(() => ({ data: null })),
          sellerAPI.getProductAnalytics().catch(() => ({ data: null })),
          categoriesAPI.getAll().catch(() => ({ data: [] })),
          badgesAPI.getUserBadges().catch(() => ({ data: null })),
          badgesAPI.getUserRewards().catch(() => ({ data: null }))
        ]);

        // Update user profile - CRITICAL FIX: Merge with localStorage to prevent data loss
        // Get user data from localStorage as fallback
        const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');

        if (userProfileData?.data) {
          const profile = userProfileData.data.profile || {};
          const businessInfo = userProfileData.data.business_info || {};
          const backendData = userProfileData.data || {};



          // CRITICAL FIX: PRIORITIZE localStorage cache to prevent data loss on refresh
          // If backend returns empty values, use cached data instead
          const firstName = cachedUser.first_name || cachedUser.firstName ||
                           cachedUser.profile?.first_name || cachedUser.profile?.firstName ||
                           profile.first_name || profile.firstName || backendData.first_name || '';

          const lastName = cachedUser.last_name || cachedUser.lastName ||
                          cachedUser.profile?.last_name || cachedUser.profile?.lastName ||
                          profile.last_name || profile.lastName || backendData.last_name || '';

          const avatarUrl = cachedUser.avatar || cachedUser.profile?.avatar ||
                           profile.avatar || backendData.avatar || '';

          const businessName = cachedUser.business_name || cachedUser.businessName ||
                              profile.business_name || businessInfo.business_name || '';

          const businessAddress = cachedUser.business_address || cachedUser.businessAddress ||
                                 profile.business_address || businessInfo.business_address || '';

          const businessPhone = cachedUser.business_phone || cachedUser.businessPhone ||
                               profile.business_phone || businessInfo.business_phone || backendData.phone || '';

          const bio = cachedUser.bio || profile.bio || '';

          // Build display name: prefer business name, then combined firstName + lastName, then username
          let displayName = '';
          if (businessName) {
            displayName = businessName;
          } else if (firstName || lastName) {
            displayName = `${firstName} ${lastName}`.trim();
          } else {
            displayName = cachedUser.username || backendData.username || cachedUser.name || 'My Shop';
          }


          const mergedProfile = {
            id: backendData.id || backendData._id || cachedUser.id,
            name: displayName,
            username: cachedUser.username || backendData.username || displayName,
            firstName: firstName,
            lastName: lastName,
            email: cachedUser.email || backendData.email || '',
            phone: cachedUser.phone || backendData.phone || '',
            avatar: avatarUrl,
            businessName: businessName,
            businessAddress: businessAddress,
            businessPhone: businessPhone,
            bio: bio,
            userType: cachedUser.user_type || backendData.user_type || 'seller',
            isVerified: profile.verification_status === 'verified' || cachedUser.isVerified || false
          };

          setUserProfile(mergedProfile);

          // Update localStorage with merged data to ensure it's always complete
          const updatedCache = {
            ...cachedUser,
            ...mergedProfile,
            // Also update both naming conventions to ensure persistence
            first_name: firstName,
            firstName: firstName,
            last_name: lastName,
            lastName: lastName,
            avatar: avatarUrl,
            business_name: businessName,
            businessName: businessName,
            business_address: businessAddress,
            businessAddress: businessAddress,
            business_phone: businessPhone,
            businessPhone: businessPhone,
            bio: bio,
            name: displayName,
            profile: {
              ...(cachedUser.profile || {}),
              first_name: firstName,
              firstName: firstName,
              last_name: lastName,
              lastName: lastName,
              avatar: avatarUrl,
              business_name: businessName,
              businessName: businessName,
              business_address: businessAddress,
              businessAddress: businessAddress,
              business_phone: businessPhone,
              businessPhone: businessPhone,
              bio: bio
            }
          };
          localStorage.setItem('user', JSON.stringify(updatedCache));
        } else {
          // Fallback to cached user data
          const firstName = cachedUser.first_name || cachedUser.firstName || '';
          const lastName = cachedUser.last_name || cachedUser.lastName || '';
          const avatarUrl = cachedUser.avatar || cachedUser.profile?.avatar || '';

          // Build display name: prefer business name, then firstName + lastName, then username
          let displayName = '';
          if (cachedUser.business_name) {
            displayName = cachedUser.business_name;
          } else if (firstName || lastName) {
            displayName = `${firstName} ${lastName}`.trim();
          } else {
            displayName = cachedUser.username || cachedUser.name || 'My Shop';
          }

          setUserProfile({
            id: cachedUser.id,
            name: displayName,
            username: cachedUser.username || displayName,
            firstName: firstName,
            lastName: lastName,
            email: cachedUser.email || '',
            phone: cachedUser.phone || '',
            avatar: avatarUrl,
            businessName: cachedUser.business_name || '',
            businessAddress: cachedUser.business_address || '',
            businessPhone: cachedUser.business_phone || '',
            bio: cachedUser.bio || '',
            userType: cachedUser.user_type || 'seller',
            isVerified: false
          });
        }

        // Process products first to get accurate counts
        let mappedProducts = [];
        let totalProductStock = 0;
        if (productsData?.data) {
          const productList = Array.isArray(productsData.data) ? productsData.data : [];
          const activeProducts = productList.filter(p => p.is_active !== false && p.deleted !== true);

          // Map products to extract proper image from images array
          mappedProducts = activeProducts.map(product => ({
            ...product,
            image: product.images && product.images.length > 0
              ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url)
              : 'https://via.placeholder.com/300x300?text=No+Image',
            name: product.title || product.name || 'Product'
          }));

          // Calculate total stock from all active products
          totalProductStock = mappedProducts.reduce((sum, product) => {
            return sum + (product.quantity || product.stock || 0);
          }, 0);

          setProducts(mappedProducts);
        }

        // Process orders to get accurate counts and totals
        let processedOrders = [];
        let pendingOrdersCount = 0;
        let completedOrdersCount = 0;
        let totalRevenue = 0;

        if (ordersData?.data) {
          const rawOrders = Array.isArray(ordersData.data) ? ordersData.data : [];

          // Map backend Order model to frontend display format
          processedOrders = rawOrders.map(order => {
            // Extract product info from items array
            let productDisplay = '';
            let totalQuantity = 0;

            if (order.items && order.items.length > 0) {
              totalQuantity = order.items.reduce((sum, item) => sum + (item.quantity || 0), 0);

              if (order.items.length === 1) {
                // Single product: "2x iPhone 13 Pro"
                const item = order.items[0];
                productDisplay = `${item.quantity}x ${item.product_title || 'Product'}`;
              } else {
                // Multiple products: "3 items"
                productDisplay = `${order.items.length} items (${totalQuantity} total)`;
              }
            } else {
              productDisplay = 'No items';
            }

            // Extract customer name from various possible fields
            const customerName = order.buyer_name
              || order.buyer?.name
              || order.buyer?.full_name
              || `${order.buyer?.first_name || ''} ${order.buyer?.last_name || ''}`.trim()
              || `${order.shipping_address?.first_name || ''} ${order.shipping_address?.last_name || ''}`.trim()
              || 'Customer';

            return {
              id: order._id || order.id,
              order_number: order.order_number,
              customer: customerName,
              // Platform privacy rule: seller sees the buyer's name only.
              // The backend no longer sends buyer_email/buyer_phone on this
              // endpoint; this used to also fall back to buyer.email and
              // shipping_address.email, either of which would have shown it
              // anyway if the backend response ever carried it.
              product: productDisplay,
              items: order.items || [],
              quantity: totalQuantity,
              amount: order.total_amount || 0,
              // Product Price − Platform Fee − VAT — what the seller
              // actually receives, never the order total (which also
              // carries logistics and the buyer's escrow fee).
              sellerPayout: order.seller_payout,
              status: order.status || 'pending',
              payment_status: order.payment_status || 'pending',
              date: order.created_at || new Date(),
              special_packaging: !!order.special_packaging,
            };
          });

          // Calculate order statistics
          pendingOrdersCount = processedOrders.filter(order =>
            order.status === 'pending' || order.status === 'processing'
          ).length;

          completedOrdersCount = processedOrders.filter(order =>
            order.status === 'completed' || order.status === 'delivered'
          ).length;

          // Calculate total revenue from completed orders
          totalRevenue = processedOrders
            .filter(order => order.status === 'completed' || order.status === 'delivered')
            .reduce((sum, order) => sum + (order.amount || 0), 0);

          setOrders(processedOrders);
        }

        // Update dashboard stats with real data
        // Priority: calculated values > backend dashboard > fallback to 0
        setStats({
          activeProducts: mappedProducts.length || dashboardData?.data?.total_products || 0,
          pendingOrders: pendingOrdersCount || dashboardData?.data?.pending_orders || 0,
          totalSales: completedOrdersCount || dashboardData?.data?.total_sales || 0,
          totalEarnings: totalRevenue || dashboardData?.data?.total_revenue || 0
        });

        // Update wallet data
        if (walletResponse?.data) {
          // Backend returns: { data: { balance: number, currency: string } }
          const balanceData = walletResponse.data.data || walletResponse.data;

          setWalletData(balanceData);
          setWalletBalance({
            balance: balanceData.balance || 0,
            heldBalance: balanceData.held_balance || balanceData.heldBalance || 0
          });

        }

        // Update analytics data with enhanced processing

        if (salesAnalytics?.data) {
          setAnalyticsData(salesAnalytics.data);

          // Update sales chart if data available
          if (salesAnalytics.data.daily_sales && Array.isArray(salesAnalytics.data.daily_sales)) {
            setSalesChartData({
              labels: salesAnalytics.data.daily_sales.map(d => d.day || d.label || d.date),
              datasets: [{
                label: 'Daily Sales (₦)',
                data: salesAnalytics.data.daily_sales.map(d => parseFloat(d.amount || d.total || 0)),
                borderColor: '#22C55E',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                tension: 0.4,
                fill: true,
              }]
            });
          } else if (salesAnalytics.data.weekly_sales && Array.isArray(salesAnalytics.data.weekly_sales)) {
            // Fallback to weekly sales if daily not available
            setSalesChartData({
              labels: salesAnalytics.data.weekly_sales.map(d => d.week || d.label || d.date),
              datasets: [{
                label: 'Weekly Sales (₦)',
                data: salesAnalytics.data.weekly_sales.map(d => parseFloat(d.amount || d.total || 0)),
                borderColor: '#22C55E',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                tension: 0.4,
                fill: true,
              }]
            });
          } else if (salesAnalytics.data.monthly_data && Array.isArray(salesAnalytics.data.monthly_data)) {
            setSalesChartData({
              labels: salesAnalytics.data.monthly_data.map(d => d.month || (d._id && `${d._id.year}-${d._id.month}`) || 'Month'),
              datasets: [{
                label: 'Monthly Sales (₦)',
                data: salesAnalytics.data.monthly_data.map(d => parseFloat(d.total_sales || d.amount || 0)),
                borderColor: '#22C55E',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                fill: true,
                tension: 0.4,
              }]
            });
          } else {
            // Generate from orders if analytics data not available
            generateSalesChartFromOrders(processedOrders);
          }

          // Update category breakdown chart
          if (salesAnalytics.data.category_breakdown && Array.isArray(salesAnalytics.data.category_breakdown)) {
            const categories = salesAnalytics.data.category_breakdown;
            setCategoryChartData({
              labels: categories.map(c => c.category || c.name),
              datasets: [{
                data: categories.map(c => parseFloat(c.amount || c.total || c.count || 0)),
                backgroundColor: ['#22C55E', '#16A34A', '#4ADE80', '#10B981', '#6B7280', '#059669'],
                borderWidth: 0,
              }]
            });
          } else if (salesAnalytics.data.top_categories && Array.isArray(salesAnalytics.data.top_categories)) {
            const categories = salesAnalytics.data.top_categories;
            setCategoryChartData({
              labels: categories.map(c => c.category || c.name),
              datasets: [{
                data: categories.map(c => parseFloat(c.sales || c.amount || c.count || 0)),
                backgroundColor: ['#22C55E', '#16A34A', '#4ADE80', '#10B981', '#6B7280', '#059669'],
                borderWidth: 0,
              }]
            });
          } else {
            // Generate from products if not available
            generateCategoryChartFromProducts(mappedProducts);
          }
        } else {
          // If no analytics data, generate from orders and products
          generateSalesChartFromOrders(processedOrders);
          generateCategoryChartFromProducts(mappedProducts);
        }

        // Update revenue/earnings chart
        if (revenueAnalytics?.data && revenueAnalytics.data.monthly_revenue && Array.isArray(revenueAnalytics.data.monthly_revenue)) {
          setMonthlyEarningsData({
            labels: revenueAnalytics.data.monthly_revenue.map(m => m.month || m.label),
            datasets: [{
              label: 'Monthly Earnings (₦)',
              data: revenueAnalytics.data.monthly_revenue.map(m => parseFloat(m.amount || m.revenue || m.total || 0)),
              backgroundColor: '#22C55E',
              borderRadius: 8,
            }]
          });
        } else {
          // Generate from orders if not available
          generateMonthlyEarningsFromOrders(processedOrders);
        }

        // Process product analytics and calculate real performance metrics

        // Calculate real performance metrics from orders and products
        const completedOrdersList = processedOrders.filter(o =>
          o.status === 'completed' || o.status === 'delivered'
        );

        // Calculate conversion rate (completed orders / total orders)
        const calculatedConversionRate = processedOrders.length > 0
          ? (completedOrdersList.length / processedOrders.length) * 100
          : 0;

        // Calculate average order value
        const calculatedTotalRevenue = completedOrdersList.reduce((sum, order) => sum + (order.amount || 0), 0);
        const calculatedAvgOrderValue = completedOrdersList.length > 0
          ? calculatedTotalRevenue / completedOrdersList.length
          : 0;

        // Calculate return rate (refunded/cancelled orders / completed orders)
        const refundedOrdersList = processedOrders.filter(o =>
          o.status === 'refunded' || o.status === 'cancelled'
        );
        const calculatedReturnRate = completedOrdersList.length > 0
          ? (refundedOrdersList.length / completedOrdersList.length) * 100
          : 0;

        // Calculate customer satisfaction (average rating from completed orders with ratings)
        const ordersWithRatings = completedOrdersList.filter(o => o.rating && o.rating > 0);
        const calculatedCustomerSatisfaction = ordersWithRatings.length > 0
          ? ordersWithRatings.reduce((sum, o) => sum + o.rating, 0) / ordersWithRatings.length
          : 4.5; // Default fallback

        // Update enhanced stats with real calculated metrics. Only
        // revenueGrowth has a real backend computation (growth_rate from
        // GetSellerSalesAnalytics — this month's revenue vs last month's);
        // orders/avgOrder/conversion growth have no backing computation
        // anywhere and are left null so the UI can hide those badges instead
        // of showing a permanent, fabricated "+0.0%".
        const realRevenueGrowth = salesAnalytics?.data?.growth_rate ?? null;
        setEnhancedStats({
          totalRevenue: calculatedTotalRevenue,
          totalOrders: completedOrdersList.length,
          averageOrderValue: calculatedAvgOrderValue,
          conversionRate: calculatedConversionRate,
          returnRate: calculatedReturnRate,
          customerSatisfaction: calculatedCustomerSatisfaction,
          revenueGrowth: realRevenueGrowth,
          ordersGrowth: null,
          avgOrderGrowth: null,
          conversionGrowth: null,
          topProducts: productAnalytics?.data?.top_products || []
        });


        // Update reports data. dashboardData (GetSellerDashboard) never
        // returns top_products/*_growth at all — this used to read them from
        // there anyway, so the Reports tab's "Top Selling Products" table was
        // always empty and every growth badge always read 0. top_products
        // comes from GetSellerProductAnalytics; revenueGrowth from
        // GetSellerSalesAnalytics's real growth_rate.
        if (dashboardData?.data) {
          setReportsData({
            totalRevenue: dashboardData.data.total_revenue || calculatedTotalRevenue,
            totalOrders: dashboardData.data.total_sales || completedOrdersList.length,
            averageOrderValue: dashboardData.data.average_order_value || calculatedAvgOrderValue,
            conversionRate: dashboardData.data.conversion_rate || calculatedConversionRate,
            revenueGrowth: realRevenueGrowth,
            ordersGrowth: null,
            avgOrderGrowth: null,
            conversionGrowth: null,
            topProducts: productAnalytics?.data?.top_products || []
          });
        } else {
          // If no dashboard data, use calculated metrics
          setReportsData({
            totalRevenue: calculatedTotalRevenue,
            totalOrders: completedOrdersList.length,
            averageOrderValue: calculatedAvgOrderValue,
            conversionRate: calculatedConversionRate,
            revenueGrowth: realRevenueGrowth,
            ordersGrowth: null,
            avgOrderGrowth: null,
            conversionGrowth: null,
            topProducts: []
          });
        }

        // Update categories
        if (categoriesData?.data) {
          const categoriesList = categoriesData.data.categories || categoriesData.data || [];
          setCategories(Array.isArray(categoriesList) ? categoriesList : []);
        }

        // Update badge progress
        if (userRewardsData?.data) {
          const rewardPoints = userRewardsData.data.points || 0;
          const userLevel = userRewardsData.data.level || calculateSellerLevel(rewardPoints);
          const { nextLevel, nextLevelPoints } = getNextLevelInfo(rewardPoints);
          const earnedBadges = userBadgesData?.data?.badges || [];

          const achievements = earnedBadges.map(badge => ({
            id: badge.badge?.id || badge.id,
            name: badge.badge?.name || 'Achievement',
            description: badge.badge?.description || '',
            earnedAt: badge.earned_at,
            icon: badge.badge?.icon || '🏆'
          }));

          const perks = getSellerLevelPerks(userLevel);

          setBadgeProgress({
            currentLevel: userLevel,
            currentPoints: rewardPoints,
            nextLevel: nextLevel,
            nextLevelPoints: nextLevelPoints,
            achievements: achievements,
            perks: perks
          });
        }

      } catch (error) {

        // Even on error, set loading to false to show the dashboard
        // Individual sections will show their own error states
        setLoading(false);

        if (error.isAuthError) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [navigate]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '₦0';
    }
    return `₦${Number(amount).toLocaleString()}`;
  };

  // Format growth percentage for reports
  // "Download Report" had no onClick at all — clicking it did nothing.
  // Exports the report data already loaded on this tab as a CSV, client-side.
  const handleDownloadReport = () => {
    const rows = [
      ['Metric', 'Value'],
      ['Total Revenue', reportsData.totalRevenue ?? 0],
      ['Total Orders', reportsData.totalOrders ?? 0],
      ['Average Order Value', reportsData.averageOrderValue ?? 0],
      ['Conversion Rate (%)', reportsData.conversionRate ?? 0],
      ['Revenue Growth (%)', reportsData.revenueGrowth ?? ''],
      [],
      ['Top Selling Products'],
      ['Product', 'Sales', 'Revenue'],
      ...(reportsData.topProducts || []).map(p => [
        p.product || p.name || p.title || p.product_title || 'Product',
        p.sales ?? p.units_sold ?? p.count ?? 0,
        p.revenue ?? p.total ?? p.amount ?? 0,
      ]),
    ];
    const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `seller-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatGrowth = (growth) => {
    if (growth === undefined || growth === null || isNaN(growth)) {
      return null; // Don't show if no data
    }
    const sign = growth >= 0 ? '+' : '';
    return `${sign}${Number(growth).toFixed(1)}% from last month`;
  };

  const formatDate = (dateValue, formatString = 'MMM dd, yyyy') => {
    if (!dateValue) {
      return 'N/A';
    }
    try {
      const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return format(date, formatString);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      shipped: '#3b82f6',
      delivered: '#22c55e',
      cancelled: '#ef4444',
      active: '#22c55e',
      out_of_stock: '#ef4444',
      draft: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pending',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      completed: 'Completed',
      cancelled: 'Cancelled',
      active: 'Active',
      out_of_stock: 'Out of Stock',
      draft: 'Draft'
    };
    return texts[status] || status;
  };

  const getPaymentStatusColor = (paymentStatus) => {
    const colors = {
      pending: '#f59e0b',
      paid: '#22c55e',
      completed: '#22c55e',
      escrow: '#3b82f6',
      failed: '#ef4444',
      refunded: '#6b7280',
      cancelled: '#9ca3af'
    };
    return colors[paymentStatus] || '#6b7280';
  };

  // `completed` covers two very different situations: a plain card/transfer
  // order that simply finished, and an escrow order whose funds the buyer
  // has now released to the seller. Sellers were told the second case still
  // read "In Escrow" (the release path never updated payment_status — fixed
  // backend-side), and once it does update, "Completed" alone doesn't say
  // the money actually moved. Pass the order and escrow-released orders read
  // "Released".
  const getPaymentStatusText = (paymentStatus, order) => {
    const texts = {
      pending: 'Pending',
      paid: 'Paid',
      completed: 'Completed',
      escrow: 'In Escrow',
      failed: 'Failed',
      refunded: 'Refunded',
      cancelled: 'Cancelled'
    };
    const key = String(paymentStatus || '').toLowerCase();
    if (key === 'completed' && (order?.use_escrow || order?.useEscrow)) return 'Released';
    return texts[key] || paymentStatus;
  };

  // Avatar Upload Handlers
  const handleAvatarFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showCustomToast({ type: 'error', title: 'Invalid File', message: 'Please select an image file' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showCustomToast({ type: 'error', title: 'File Too Large', message: 'Image size should be less than 5MB' });
        return;
      }
      setSelectedAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = async () => {
    try {
      setUploadingAvatar(true);
      let avatarUrl = '';

      if (avatarUploadMethod === 'url') {
        // Use the preview URL that user typed in
        avatarUrl = avatarPreview || '';
        if (!avatarUrl || !avatarUrl.startsWith('http')) {
          showCustomToast({
            type: 'warning',
            title: 'Invalid URL',
            message: 'Please enter a valid image URL starting with http:// or https://'
          });
          setUploadingAvatar(false);
          return;
        }
      } else {
        // Upload file to image hosting service (ImgBB/Imgur)
        if (!selectedAvatarFile) {
          showCustomToast({
            type: 'warning',
            title: 'No File Selected',
            message: 'Please select an image file'
          });
          setUploadingAvatar(false);
          return;
        }

        const uploadResult = await uploadImage(selectedAvatarFile, (progress) => {
        });

        if (uploadResult && uploadResult.url) {
          avatarUrl = uploadResult.url;
        } else {
          throw new Error('Failed to upload image - no URL returned');
        }
      }

      // CRITICAL: Use userAPI.updateProfile for avatar (not sellerAPI)
      // Include existing name fields to prevent data wipe on PUT
      const response = await userAPI.updateProfile({
        first_name: userProfile?.firstName || '',
        last_name: userProfile?.lastName || '',
        avatar: avatarUrl,
      });

      // Update userProfile state
      const updatedProfile = { ...userProfile, avatar: avatarUrl };
      setUserProfile(updatedProfile);

      // CRITICAL: Update localStorage with avatar to persist after refresh
      const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedCache = {
        ...cachedUser,
        avatar: avatarUrl,
        profile: {
          ...(cachedUser.profile || {}),
          avatar: avatarUrl
        }
      };
      localStorage.setItem('user', JSON.stringify(updatedCache));

      setAvatarPreview(avatarUrl);
      setSelectedAvatarFile(null);
      // Notify Navbar/AuthContext so avatar updates live
      window.dispatchEvent(new Event('userUpdated'));
      showCustomToast({
        type: 'success',
        title: 'Profile Picture Updated',
        message: avatarUploadMethod === 'file'
          ? `Your profile picture has been uploaded and saved successfully!`
          : 'Your profile picture has been saved successfully!'
      });
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Update Failed',
        message: error.message || 'Failed to update profile picture. Please try again.'
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Profile Edit Handlers

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setSettingsFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileSave = async () => {
    setIsSavingSettings(true);
    try {
      // CRITICAL: Split the update into TWO API calls
      // 1. User profile fields (first_name, last_name) -> userAPI
      // 2. Business fields (business_name, etc.) -> sellerAPI

      const userProfileData = {
        first_name: settingsFormData.firstName,
        last_name: settingsFormData.lastName
      };

      const businessProfileData = {
        business_name: settingsFormData.businessName,
        business_address: settingsFormData.businessAddress,
        business_phone: settingsFormData.businessPhone,
        bio: settingsFormData.bio
      };


      // Update both profiles in parallel
      const [userResponse, sellerResponse] = await Promise.all([
        userAPI.updateProfile(userProfileData),
        sellerAPI.updateProfile(businessProfileData)
      ]);


      // Build updated display name
      let updatedDisplayName = '';
      if (settingsFormData.businessName) {
        updatedDisplayName = settingsFormData.businessName;
      } else if (settingsFormData.firstName || settingsFormData.lastName) {
        updatedDisplayName = `${settingsFormData.firstName} ${settingsFormData.lastName}`.trim();
      } else {
        updatedDisplayName = userProfile.username || 'My Shop';
      }

      const updatedProfile = {
        ...userProfile,
        firstName: settingsFormData.firstName,
        lastName: settingsFormData.lastName,
        phone: settingsFormData.phone,
        businessName: settingsFormData.businessName,
        businessAddress: settingsFormData.businessAddress,
        businessPhone: settingsFormData.businessPhone,
        bio: settingsFormData.bio,
        name: updatedDisplayName
      };

      setUserProfile(updatedProfile);

      // CRITICAL: Update localStorage with complete merged data to persist after refresh
      const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedCache = {
        ...cachedUser,
        ...updatedProfile,
        // Support both naming conventions (snake_case and camelCase)
        first_name: settingsFormData.firstName,
        firstName: settingsFormData.firstName,
        last_name: settingsFormData.lastName,
        lastName: settingsFormData.lastName,
        business_name: settingsFormData.businessName,
        businessName: settingsFormData.businessName,
        business_address: settingsFormData.businessAddress,
        businessAddress: settingsFormData.businessAddress,
        business_phone: settingsFormData.businessPhone,
        businessPhone: settingsFormData.businessPhone,
        bio: settingsFormData.bio,
        name: updatedDisplayName,
        profile: {
          ...(cachedUser.profile || {}),
          first_name: settingsFormData.firstName,
          firstName: settingsFormData.firstName,
          last_name: settingsFormData.lastName,
          lastName: settingsFormData.lastName,
          business_name: settingsFormData.businessName,
          businessName: settingsFormData.businessName,
          business_address: settingsFormData.businessAddress,
          businessAddress: settingsFormData.businessAddress,
          business_phone: settingsFormData.businessPhone,
          businessPhone: settingsFormData.businessPhone,
          bio: settingsFormData.bio
        }
      };
      localStorage.setItem('user', JSON.stringify(updatedCache));

      showCustomToast({
        type: 'success',
        title: 'Profile Updated',
        message: 'Your profile has been saved successfully and will persist after refresh!'
      });
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Update Failed',
        message: error.message || 'Failed to update profile. Please try again.'
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showCustomToast({ type: 'error', title: 'Validation Error', message: 'New passwords do not match' });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      showCustomToast({ type: 'error', title: 'Validation Error', message: 'Password must be at least 6 characters' });
      return;
    }
    try {
      setSavingPassword(true);
      await userAPI.changePassword({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showCustomToast({ type: 'success', title: 'Password Updated', message: 'Password updated successfully!' });
    } catch (error) {
      showCustomToast({ type: 'error', title: 'Update Failed', message: error?.response?.data?.message || error.message || 'Failed to update password.' });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    try {
      setDeletingAccount(true);
      await userAPI.deleteAccount(passwordForDeletion);
      localStorage.clear();
      window.location.href = '/';
    } catch (error) {
      showCustomToast({ type: 'error', title: 'Delete Failed', message: error?.response?.data?.message || 'Failed to delete account.' });
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleExportData = async () => {
    try {
      setExportingData(true);
      const { dataManagementAPI } = await import('../services/api');
      await dataManagementAPI.exportUserData();
      showCustomToast({ type: 'success', title: 'Export Requested', message: 'Your data export will be sent to your email.' });
    } catch {
      showCustomToast({ type: 'error', title: 'Export Failed', message: 'Failed to request data export.' });
    } finally {
      setExportingData(false);
    }
  };


  const handleProductInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewProduct(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);

    // Validate number of images
    if (productImages.length + files.length > 10) {
      showCustomToast({
        type: 'warning',
        title: 'Too Many Images',
        message: `You can only upload up to 10 images. Currently: ${productImages.length}`
      });
      return;
    }

    // Validate each file
    const validFiles = [];
    for (const file of files) {
      const validation = validateImageFile(file, {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      });

      if (!validation.valid) {
        showCustomToast({
          type: 'error',
          title: 'Invalid File',
          message: `${file.name}: ${validation.error}`
        });
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) return;

    // Upload images
    setUploadingImages(true);
    setUploadProgress({ current: 0, total: validFiles.length, fileName: '' });

    try {
      const uploadedUrls = await uploadMultipleImages(validFiles, (progress) => {
        setUploadProgress({
          current: progress.index + 1,
          total: progress.total,
          fileName: progress.fileName,
          status: progress.status
        });

        if (progress.status === 'error') {
          showCustomToast({
            type: 'error',
            title: 'Upload Failed',
            message: `Failed to upload ${progress.fileName}: ${progress.error}`
          });
        }
      });

      // Filter out any undefined/null URLs (failed uploads)
      const successfulUrls = uploadedUrls.filter(url => url);

      if (successfulUrls.length > 0) {
        setProductImages(prev => [...prev, ...successfulUrls].slice(0, 10));
        showCustomToast({
          type: 'success',
          title: 'Upload Complete',
          message: `Successfully uploaded ${successfulUrls.length} image(s)`
        });
      }

      if (successfulUrls.length < validFiles.length) {
        showCustomToast({
          type: 'warning',
          title: 'Partial Upload',
          message: `${validFiles.length - successfulUrls.length} image(s) failed to upload`
        });
      }
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Upload Failed',
        message: error.message || 'Failed to upload images. Please try again.'
      });
    } finally {
      setUploadingImages(false);
      setUploadProgress({ current: 0, total: 0, fileName: '' });
      // Reset file input
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    setProductImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddImageUrl = () => {
    if (imageUrlInput.trim() && productImages.length < 10) {
      // Validate URL format
      try {
        new URL(imageUrlInput);
        setProductImages(prev => [...prev, imageUrlInput.trim()]);
        setImageUrlInput('');
      } catch (error) {
        showCustomToast({ type: 'warning', title: 'Invalid Input', message: 'Please enter a valid URL' });
      }
    }
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    try {
      // Block non-admin users from posting in Accessories category
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (newProduct.category === 'accessories' && currentUser.user_type !== 'admin') {
        showCustomToast({ type: 'error', title: 'Access Denied', message: 'Only admins can post in the Accessories category' });
        return;
      }

      // Validate required fields
      if (!newProduct.title || newProduct.title.length < 5) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Title must be at least 5 characters' });
        return;
      }
      if (!newProduct.description || newProduct.description.length < 20) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Description must be at least 20 characters' });
        return;
      }
      if (!newProduct.price || parseFloat(newProduct.price) <= 0) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Price must be greater than 0' });
        return;
      }
      // Validate seller price if provided
      if (newProduct.sellerPrice && parseFloat(newProduct.sellerPrice) <= 0) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Wholesale price must be greater than 0' });
        return;
      }
      if (newProduct.sellerPrice && parseFloat(newProduct.sellerPrice) >= parseFloat(newProduct.price)) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Wholesale price must be less than regular price' });
        return;
      }
      if (!newProduct.category) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Please select a category' });
        return;
      }
      if (!newProduct.location) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Please enter location' });
        return;
      }

      // Prepare product data for backend (matching CreateProductRequest)
      const productData = {
        title: newProduct.title,
        description: newProduct.description,
        price: parseFloat(newProduct.price),
        currency: 'NGN', // Nigerian Naira
        condition: newProduct.condition,
        brand: newProduct.brand || '',
        model: newProduct.model || '',
        color: newProduct.colors?.[0] || newProduct.color || '',
        colors: newProduct.colors || [],
        category_id: newProduct.category, // This should be a valid category ObjectID
        specifications: newProduct.specifications ?
          { details: newProduct.specifications } : {},
        quantity: parseInt(newProduct.quantity) || 1,
        status: 'active', // Set status to active instead of draft
        location: {
          city: newProduct.location.split(',')[0]?.trim() || newProduct.location,
          state: newProduct.location.split(',')[1]?.trim() || 'Lagos',
          country: 'Nigeria'
        },
        swap_available: newProduct.swapAvailable || false,
        tags: newProduct.tags ? newProduct.tags.split(',').map(t => t.trim()) : [],
        // Only send actual URLs, filter out blob URLs (local previews)
        images: productImages.length > 0
          ? productImages
              .filter(img => {
                const imgStr = typeof img === 'string' ? img : img?.url || '';
                return !imgStr.startsWith('blob:');
              })
              .map(img => typeof img === 'string' ? img : img?.url || img)
          : []
      };

      // Only add seller_price if it has a value
      if (newProduct.sellerPrice && newProduct.sellerPrice.trim() !== '') {
        productData.seller_price = parseFloat(newProduct.sellerPrice);
      }


      const response = await sellerAPI.addProduct(productData);

      if (response?.data) {
        showCustomToast({ type: 'success', title: 'Success', message: 'Product added successfully!' });
        setShowAddProductForm(false);
        setNewProduct({
          title: '',
          category: '',
          brand: '',
          model: '',
          condition: 'new',
          price: '',
          sellerPrice: '',
          description: '',
          specifications: '',
          quantity: '',
          location: '',
          swapAvailable: false,
          tags: ''
        });
        setProductImages([]);

        // Refresh products list
        const productsData = await sellerAPI.getProducts();
        if (productsData?.data) {
          // Filter out deleted/inactive products
          const activeProducts = productsData.data.filter(p => p.is_active !== false && p.deleted !== true);
          // Map products to extract proper image from images array
          const mappedProducts = activeProducts.map(product => ({
            ...product,
            image: product.images && product.images.length > 0
              ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url)
              : 'https://via.placeholder.com/300x300?text=No+Image',
            name: product.title || product.name || 'Product'
          }));
          setProducts(mappedProducts);
          // Update stats to show only active products count
          setStats(prev => ({ ...prev, activeProducts: mappedProducts.length }));
        }
      }
    } catch (error) {
      showCustomToast({ type: 'error', title: 'Error', message: 'Failed to add product. Please try again.' });
    }
  };

  // Handle product deletion
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      return;
    }

    try {

      // Optimistically remove from local state for instant feedback
      setProducts(prevProducts => {
        const filtered = prevProducts.filter(p => (p._id || p.id) !== productId);
        // Update stats count
        setStats(prev => ({ ...prev, activeProducts: filtered.length }));
        return filtered;
      });

      // Call backend API to delete
      await sellerAPI.deleteProduct(productId);

      showCustomToast({ type: 'success', title: 'Success', message: 'Product deleted successfully!' });
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: `Failed to delete product: ${error.message || 'Please try again.'}`
      });

      // Revert by refetching from backend
      try {
        const productsData = await sellerAPI.getProducts();
        if (productsData?.data) {
          const productList = Array.isArray(productsData.data) ? productsData.data : [];
          const activeProducts = productList.filter(p => p.is_active !== false && p.deleted !== true);
          const mappedProducts = activeProducts.map(product => ({
            ...product,
            image: product.images && product.images.length > 0
              ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url)
              : 'https://via.placeholder.com/300x300?text=No+Image',
            name: product.title || product.name || 'Product'
          }));
          setProducts(mappedProducts);
          setStats(prev => ({ ...prev, activeProducts: mappedProducts.length }));
        }
      } catch (refetchError) {
      }
    }
  };

  // Handle view product details
  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setShowViewProductModal(true);
  };

  // Handle edit product
  const handleEditProduct = (product) => {
    // Extract category ID properly
    let categoryId = '';
    if (product.category_id) {
      categoryId = typeof product.category_id === 'object' ? product.category_id._id || product.category_id.id : product.category_id;
    } else if (product.category) {
      categoryId = typeof product.category === 'object' ? product.category._id || product.category.id : product.category;
    }

    setEditProduct({
      _id: product._id || product.id,
      title: product.title || product.name,
      category: categoryId,
      brand: product.brand || '',
      model: product.model || '',
      color: product.color || '',
      colors: product.colors?.length ? product.colors : (product.color ? [product.color] : []),
      condition: product.condition || 'new',
      price: product.price || '',
      sellerPrice: product.seller_price || product.sellerPrice || '',
      description: product.description || '',
      specifications: product.specifications?.details || product.specifications || '',
      quantity: product.quantity || product.stock || '',
      location: product.location?.city ? `${product.location.city}, ${product.location.state}` : product.location || '',
      swapAvailable: product.swap_available || false,
      tags: product.tags ? product.tags.join(', ') : '',
      images: product.images || []
    });
    setProductImages(product.images || []);
    setShowEditProductModal(true);
  };

  // Handle update product
  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      // Validate required fields
      if (!editProduct.title || editProduct.title.length < 5) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Title must be at least 5 characters' });
        return;
      }
      if (!editProduct.description || editProduct.description.length < 20) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Description must be at least 20 characters' });
        return;
      }
      if (!editProduct.price || parseFloat(editProduct.price) <= 0) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Price must be greater than 0' });
        return;
      }
      // Validate seller price if provided
      if (editProduct.sellerPrice && parseFloat(editProduct.sellerPrice) <= 0) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Wholesale price must be greater than 0' });
        return;
      }
      if (editProduct.sellerPrice && parseFloat(editProduct.sellerPrice) >= parseFloat(editProduct.price)) {
        showCustomToast({ type: 'warning', title: 'Validation Error', message: 'Wholesale price must be less than regular price' });
        return;
      }

      // Validate images - warn if no images
      const hasExistingImages = editProduct.images && editProduct.images.length > 0;
      const hasNewImages = productImages && productImages.length > 0;

      if (!hasExistingImages && !hasNewImages) {
        showCustomToast({
          type: 'warning',
          title: 'No Images',
          message: 'Please add at least one product image before updating',
          duration: 4000
        });
        return;
      }

      // Extract category ID as string
      let categoryId = editProduct.category;
      if (typeof categoryId === 'object' && categoryId !== null) {
        categoryId = categoryId._id || categoryId.id || '';
      }

      const productData = {
        title: editProduct.title,
        description: editProduct.description,
        price: parseFloat(editProduct.price),
        currency: 'NGN',
        condition: editProduct.condition,
        brand: editProduct.brand || '',
        model: editProduct.model || '',
        color: editProduct.colors?.[0] || editProduct.color || '',
        colors: editProduct.colors || [],
        category_id: categoryId,
        specifications: editProduct.specifications ?
          { details: editProduct.specifications } : {},
        quantity: parseInt(editProduct.quantity) || 1,
        status: 'active',
        location: typeof editProduct.location === 'string'
          ? {
              city: editProduct.location.split(',')[0]?.trim() || editProduct.location || 'Lagos',
              state: editProduct.location.split(',')[1]?.trim() || 'Lagos',
              country: 'Nigeria'
            }
          : {
              city: editProduct.location?.city || 'Lagos',
              state: editProduct.location?.state || 'Lagos',
              country: editProduct.location?.country || 'Nigeria'
            },
        swap_available: editProduct.swapAvailable || false,
        tags: editProduct.tags ? editProduct.tags.split(',').map(t => t.trim()) : [],
        images: productImages.length > 0
          ? productImages
              .filter(img => {
                const imgStr = typeof img === 'string' ? img : img?.url || '';
                return !imgStr.startsWith('blob:');
              })
              .map(img => typeof img === 'string' ? img : img?.url || img)
          : (editProduct.images || []).map(img => typeof img === 'string' ? img : img?.url || img)
      };

      // Only add seller_price if it has a value
      if (editProduct.sellerPrice && String(editProduct.sellerPrice).trim() !== '') {
        productData.seller_price = parseFloat(editProduct.sellerPrice);
      }

      const productId = editProduct._id || editProduct.id;

      const response = await sellerAPI.updateProduct(productId, productData);

      // Update product in local state with actual data
      setProducts(prevProducts =>
        prevProducts.map(p => {
          if ((p._id || p.id) === productId) {
            return {
              ...p,
              ...productData,
              image: productImages.length > 0
                ? (typeof productImages[0] === 'string' ? productImages[0] : productImages[0]?.url)
                : p.image,
              name: productData.title,
              title: productData.title
            };
          }
          return p;
        })
      );

      showCustomToast({ type: 'success', title: 'Success', message: 'Product updated successfully! Changes will persist after refresh.' });
      setShowEditProductModal(false);
      setEditProduct(null);
      setProductImages([]);
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: `Failed to update product: ${error.message || 'Please try again.'}`
      });
    }
  };

  // Handle order status update
  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await sellerAPI.updateOrderStatus(orderId, newStatus);
      showCustomToast({ type: 'success', title: 'Success', message: 'Order status updated successfully!' });

      // Refresh orders list
      const ordersData = await sellerAPI.getOrders();
      if (ordersData?.data) {
        setOrders(ordersData.data);
      }
    } catch (error) {
      showCustomToast({ type: 'error', title: 'Error', message: 'Failed to update order status. Please try again.' });
    }
  };

  // Handle notification mark as read
  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await markAsRead(notificationId);
    } catch (error) {
    }
  };

  // Handle logout
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      // Clear auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');

      // Redirect to auth page
      navigate('/auth');
    }
  };


  // Fetch wallet balance (matching mobile app implementation)
  const fetchWalletBalance = async (showNotification = false) => {
    try {
      const response = await walletAPI.getBalance();

      const fwbPayload = response?.data || response;
      if (fwbPayload) {
        const balanceData = fwbPayload.balance !== undefined ? fwbPayload : (fwbPayload.data || fwbPayload);

        // Parse all balance fields properly (matching mobile app structure)
        const availableBalance = parseFloat(balanceData.balance) || 0;
        // ESCROW DISABLED: const escrowBalance = parseFloat(balanceData.escrow_balance || ...);
        const heldBalance = parseFloat(balanceData.held_balance || balanceData.heldBalance || 0);
        const pendingBalance = parseFloat(
          balanceData.pending_balance ||
          balanceData.pendingBalance ||
          0
        );

        const newBalance = {
          balance: availableBalance,
          heldBalance: heldBalance,
          pendingBalance: pendingBalance,
          totalBalance: availableBalance + heldBalance + pendingBalance,
          currency: balanceData.currency || 'NGN'
        };

        setWalletBalance(newBalance);

        if (showNotification) {
          showCustomToast({ type: 'success', title: 'Success', message: `Balance: ₦${availableBalance.toLocaleString()}` });
        }
      }
    } catch (error) {
      if (showNotification) {
        showCustomToast({ type: 'error', title: 'Error', message: 'Failed to load balance' });
      }
    }
  };

  // Fetch wallet transactions (matching mobile app implementation)
  const fetchWalletTransactions = async () => {
    setIsLoadingWallet(true);
    try {
      const response = await walletAPI.getTransactions({ limit: 50 });

      const txPayload = response?.data || response;
      if (txPayload) {
        let transactions = [];
        if (Array.isArray(txPayload)) {
          transactions = txPayload;
        } else if (Array.isArray(txPayload.transactions)) {
          transactions = txPayload.transactions;
        } else if (txPayload.data && Array.isArray(txPayload.data)) {
          transactions = txPayload.data;
        }

        transactions.sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt).getTime();
          const dateB = new Date(b.created_at || b.createdAt).getTime();
          return dateB - dateA;
        });

        setWalletTransactions(transactions);
      } else {
        setWalletTransactions([]);
      }
    } catch (error) {
      setWalletTransactions([]);
      showCustomToast({ type: 'error', title: 'Error', message: 'Failed to load transactions' });
    } finally {
      setIsLoadingWallet(false);
    }
  };

  // Fetch user's saved bank accounts
  const fetchUserBankAccounts = async () => {
    try {
      setLoadingBankAccounts(true);
      const response = await userAPI.getBankAccounts();
      const data = response.data || response;
      setUserBankAccounts(data.bank_accounts || []);
    } catch (error) {
      setUserBankAccounts([]);
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  // Handle withdrawal form changes
  const handleWithdrawalFormChange = (e) => {
    const { name, value } = e.target;
    setWithdrawalForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle bank account selection from dropdown
  const handleBankAccountSelect = (e) => {
    const selectedId = e.target.value;
    const selectedAccount = userBankAccounts.find(acc => (acc.id || acc._id) === selectedId);

    if (selectedAccount) {
      setWithdrawalForm(prev => ({
        ...prev,
        selectedBankId: selectedId,
        bankAccount: selectedAccount.account_number,
        bankName: selectedAccount.bank_name,
        accountName: selectedAccount.account_name
      }));
    } else {
      // Manual entry
      setWithdrawalForm(prev => ({
        ...prev,
        selectedBankId: '',
        bankAccount: '',
        bankName: '',
        accountName: ''
      }));
    }
  };

  // Handle withdrawal submission
  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();

    // Validate amount
    const amount = parseFloat(withdrawalForm.amount);
    if (isNaN(amount) || amount < 1000) {
      showCustomToast({ type: 'warning', title: 'Invalid Amount', message: 'Minimum withdrawal amount is ₦1,000' });
      return;
    }

    if (amount > walletBalance.balance) {
      showCustomToast({ type: 'error', title: 'Insufficient Balance', message: 'Insufficient wallet balance for this withdrawal' });
      return;
    }

    // bank_code is required by the backend to initiate the disbursement, and
    // only a saved bank account carries one — a manually typed bank name has
    // no code, so the request would be rejected before reaching the gateway.
    const selectedAccount = userBankAccounts.find(
      acc => (acc.id || acc._id) === withdrawalForm.selectedBankId
    );
    if (!selectedAccount?.bank_code) {
      showCustomToast({
        type: 'warning',
        title: 'Bank Account Required',
        message: 'Select a saved bank account to withdraw to. Add one under Bank Accounts if you have none.'
      });
      return;
    }

    setIsProcessingWithdrawal(true);
    try {
      const response = await walletAPI.requestWithdrawal({
        amount: amount,
        bank_account: withdrawalForm.bankAccount,
        bank_name: withdrawalForm.bankName,
        bank_code: selectedAccount.bank_code,
        account_name: withdrawalForm.accountName
      });

      if (response) {
        showCustomToast({ type: 'success', title: 'Withdrawal Submitted', message: 'Withdrawal request submitted successfully!' });
        setShowWithdrawModal(false);
        setWithdrawalForm({
          amount: '',
          bankAccount: '',
          bankName: '',
          accountName: ''
        });

        // Refresh wallet balance
        const walletResponse = await walletAPI.getBalance();
        const refreshedBal = walletResponse?.data || walletResponse;
        if (refreshedBal) {
          const balanceData = refreshedBal.balance !== undefined ? refreshedBal : (refreshedBal.data || refreshedBal);
          setWalletBalance({
            balance: balanceData.balance || 0,
            heldBalance: balanceData.held_balance || balanceData.heldBalance || 0
          });
        }

        // Refresh transactions
        fetchWalletTransactions();
      }
    } catch (error) {
      showCustomToast({ type: 'error', title: 'Withdrawal Failed', message: 'Failed to request withdrawal. Please try again.' });
    } finally {
      setIsProcessingWithdrawal(false);
    }
  };

  // Handle wallet top-up
  const handleWalletTopup = async () => {
    const amount = parseFloat(topupAmount);

    if (!amount || amount < 100) {
      showCustomToast({ type: 'warning', title: 'Invalid Amount', message: 'Minimum top-up amount is ₦100' });
      return;
    }

    try {
      setIsProcessingTopup(true);

      const { paymentAPI } = await import('../services/api');

      const paymentData = {
        amount: parseInt(amount),
        payment_type: 'wallet_topup',
        currency: 'NGN',
        callback_url: `${window.location.origin}/premium-seller-dashboard?payment=success`,
        metadata: {
          topup_amount: parseInt(amount)
        }
      };

      const paymentResponse = await paymentAPI.initializePayment(paymentData);

      if (paymentResponse && paymentResponse.data && paymentResponse.data.checkout_url) {
        localStorage.setItem('pending_payment_id', paymentResponse.data.payment_id);
        localStorage.setItem('pending_payment_reference', paymentResponse.data.reference);
        window.location.href = paymentResponse.data.checkout_url;
      } else {
        showCustomToast({ type: 'error', title: 'Payment Error', message: 'Failed to initialize payment' });
      }
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Top-up Failed',
        message: error.message || 'Failed to process top-up'
      });
    } finally {
      setIsProcessingTopup(false);
    }
  };

  // VIP Support Handlers
  const handleVipTicketFormChange = (e) => {
    const { name, value } = e.target;
    setVipTicketForm(prev => ({ ...prev, [name]: value }));
  };

  const handleVipTicketSubmit = async (e) => {
    e.preventDefault();

    if (!vipTicketForm.subject.trim() || !vipTicketForm.message.trim()) {
      showCustomToast({
        type: 'warning',
        title: 'Validation Error',
        message: 'Please fill in all fields'
      });
      return;
    }

    setIsSubmittingVipTicket(true);
    try {
      const response = await supportAPI.createVIPTicket({
        subject: vipTicketForm.subject,
        priority: vipTicketForm.priority,
        message: vipTicketForm.message,
        category: 'vip_support'
      });

      if (response) {
        showCustomToast({
          type: 'success',
          title: 'Success',
          message: 'VIP support ticket created successfully! Our team will respond shortly.'
        });

        setVipTicketForm({
          subject: '',
          priority: 'medium',
          message: ''
        });
      }
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to create support ticket. Please try again.'
      });
    } finally {
      setIsSubmittingVipTicket(false);
    }
  };

  const handleVipCallSupport = () => {
    showCustomToast({
      type: 'info',
      title: 'Calling VIP Support',
      message: 'Connecting you to our VIP support line: +234 800 123 4567'
    });
    window.location.href = 'tel:+2348001234567';
  };

  const handleVipEmailSupport = () => {
    window.location.href = 'mailto:vip@AutoboyExpress.com?subject=VIP Support Request';
  };

  const handleVipLiveChat = () => {
    navigate('/support-chat');
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: isDarkMode ? '#9CA3AF' : '#6B7280',
        }
      },
      y: {
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: isDarkMode ? '#9CA3AF' : '#6B7280',
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: isDarkMode ? '#F3F4F6' : '#1F2937',
          padding: 20,
          usePointStyle: true,
        }
      }
    }
  };

  // Dark mode color helper
  const getColor = (lightColor, darkColor) => isDarkMode ? darkColor : lightColor;

  const renderDashboardContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      {/* KYC Status Banner */}
      <KYCStatusBanner onNavigateToKYC={() => setActiveSection('kyc')} />

      {/* Ultra-Modern Premium Welcome Banner with Glassmorphism */}
      <div style={{
        background: getColor(
          'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.15) 100%)',
          'linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)'
        ),
        backdropFilter: 'blur(20px)',
        border: getColor('1px solid rgba(34, 197, 94, 0.2)', '1px solid rgba(34, 197, 94, 0.15)'),
        borderRadius: '24px',
        padding: '2rem',
        marginBottom: '2rem',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: getColor('0 8px 32px rgba(0, 0, 0, 0.1)', '0 8px 32px rgba(0, 0, 0, 0.3)')
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '400px',
          height: '400px',
          background: getColor(
            'radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)',
            'radial-gradient(circle, rgba(34, 197, 94, 0.08) 0%, transparent 70%)'
          ),
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.5rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(255, 215, 0, 0.4)',
                animation: 'pulse 2s ease-in-out infinite'
              }}>
                <FontAwesomeIcon icon={faCrown} style={{ fontSize: '28px', color: '#000' }} />
              </div>
              <div>
                <h2 style={{
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  marginBottom: '0.25rem',
                  background: getColor(
                    'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                    'linear-gradient(135deg, #E5E7EB 0%, #F9FAFB 100%)'
                  ),
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Welcome back, {getDisplayName(userProfile.name || userProfile.firstName)}!
                </h2>
                <p style={{ fontSize: '0.95rem', color: getColor('#6B7280', '#9CA3AF'), fontWeight: '500' }}>
                  <FontAwesomeIcon icon={faTrophy} style={{ marginRight: '0.5rem', color: '#FFD700' }} />
                  {badgeProgress.currentLevel} Seller • {badgeProgress.currentPoints} Points
                  <span style={{ margin: '0 0.5rem', color: getColor('#D1D5DB', '#4B5563') }}>•</span>
                  {badgeProgress.nextLevelPoints - badgeProgress.currentPoints} to {badgeProgress.nextLevel}
                </p>
              </div>
            </div>
            <div style={{
              background: getColor('rgba(34, 197, 94, 0.1)', 'rgba(34, 197, 94, 0.15)'),
              border: getColor('1px solid rgba(34, 197, 94, 0.2)', '1px solid rgba(34, 197, 94, 0.25)'),
              borderRadius: '12px',
              padding: '0.75rem 1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem'
            }}>
              <FontAwesomeIcon icon={faStore} style={{ color: '#22C55E', fontSize: '18px' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: '600', color: getColor('#1F2937', '#F9FAFB') }}>
                {stats.activeProducts} Active Products • {stats.totalSales} Total Sales
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setActiveSection('products')}
              style={{
                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '0.875rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(34, 197, 94, 0.3)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 24px rgba(34, 197, 94, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(34, 197, 94, 0.3)';
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Product
            </button>
            <button
              onClick={() => setActiveSection('wallet')}
              style={{
                background: getColor('rgba(255, 255, 255, 0.9)', 'rgba(20, 20, 20, 0.95)'),
                color: getColor('#1F2937', '#F9FAFB'),
                border: getColor('1px solid rgba(209, 213, 219, 0.5)', '1px solid rgba(75, 85, 99, 0.4)'),
                borderRadius: '12px',
                padding: '0.875rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <FontAwesomeIcon icon={faWallet} />
              My Wallet
            </button>
          </div>
        </div>
      </div>

      {/* Flash Deals — premium sellers see new deals as soon as they're posted,
          same as premium buyers; everyone else waits out the early-access window. */}
      <div style={{
        background: getColor('rgba(255, 255, 255, 0.8)', 'rgba(20, 20, 20, 0.95)'),
        backdropFilter: 'blur(20px)',
        border: getColor('1px solid rgba(209, 213, 219, 0.3)', '1px solid rgba(60, 60, 60, 0.5)'),
        borderRadius: '20px',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.1rem' }}>
          <FontAwesomeIcon icon={faBolt} style={{ color: '#22C55E' }} />
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: getColor('#1F2937', '#F9FAFB') }}>Flash Deals</h3>
        </div>
        <FlashDealsStrip isDarkMode={isDarkMode} />
      </div>

      {/* Modern Stats Cards with Gradient Borders & Animations */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.25rem',
        marginBottom: '2rem'
      }}>
        {[
          { icon: faMobileAlt, value: stats.activeProducts, label: 'Active Products', gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)', color: '#3B82F6' },
          { icon: faShoppingCart, value: stats.pendingOrders, label: 'Pending Orders', gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', color: '#F59E0B' },
          { icon: faCheckCircle, value: stats.totalSales, label: 'Total Sales', gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#22C55E' },
          { icon: faNairaSign, value: formatCurrency(stats.totalEarnings), label: 'Total Earnings', gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: '#8B5CF6' }
        ].map((stat, idx) => (
          <div
            key={idx}
            style={{
              background: getColor('rgba(255, 255, 255, 0.8)', 'rgba(20, 20, 20, 0.95)'),
              backdropFilter: 'blur(20px)',
              border: getColor('1px solid rgba(209, 213, 219, 0.3)', '1px solid rgba(60, 60, 60, 0.5)'),
              borderRadius: '20px',
              padding: '1.75rem',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              boxShadow: getColor('0 4px 12px rgba(0, 0, 0, 0.05)', '0 4px 12px rgba(0, 0, 0, 0.5)')
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = isDarkMode ? '0 12px 32px rgba(0, 0, 0, 0.5)' : '0 12px 32px rgba(0, 0, 0, 0.12)';
              e.currentTarget.style.borderColor = stat.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.05)';
              e.currentTarget.style.borderColor = isDarkMode ? 'rgba(60, 60, 60, 0.5)' : 'rgba(209, 213, 219, 0.3)';
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-30%',
              right: '-20%',
              width: '120px',
              height: '120px',
              background: stat.gradient,
              borderRadius: '50%',
              opacity: 0.1,
              filter: 'blur(40px)'
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: stat.gradient,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem',
                boxShadow: `0 4px 16px ${stat.color}40`
              }}>
                <FontAwesomeIcon icon={stat.icon} style={{ fontSize: '20px', color: 'white' }} />
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.25rem', color: getColor('#1F2937', '#F9FAFB') }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '0.9rem', color: getColor('#6B7280', '#9CA3AF'), fontWeight: '500' }}>
                {stat.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))', gap: '1.5rem' }}>
        {/* Quick Actions - Enhanced Modern Card Grid */}
        <div style={{
          background: getColor('rgba(255, 255, 255, 0.9)', 'rgba(18, 18, 18, 0.98)'),
          backdropFilter: 'blur(20px)',
          border: getColor('1px solid rgba(209, 213, 219, 0.3)', '1px solid rgba(50, 50, 50, 0.6)'),
          borderRadius: '20px',
          padding: '1.75rem',
          boxShadow: getColor('0 4px 12px rgba(0, 0, 0, 0.05)', '0 4px 12px rgba(0, 0, 0, 0.6)')
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FontAwesomeIcon icon={faBolt} style={{ color: 'white', fontSize: '18px' }} />
            </div>
            <h2 style={{ fontSize: '1.35rem', fontWeight: '700', color: getColor('#1F2937', '#F9FAFB'), margin: 0 }}>
              Quick Actions
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            {[
              { icon: faPlus, label: 'Add Product', section: 'products', gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#22C55E' },
              { icon: faTruck, label: 'Manage Orders', section: 'orders', gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)', color: '#3B82F6' },
              { icon: faChartPie, label: 'Analytics', section: 'analytics', gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: '#8B5CF6' },
              { icon: faWallet, label: 'My Wallet', section: 'wallet', gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', color: '#F59E0B' },
              { icon: faChartLine, label: 'Reports', section: 'reports', gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', color: '#EF4444' }
            ].map((action, idx) => (
              <button
                key={idx}
                onClick={() => setActiveSection(action.section)}
                style={{
                  background: getColor('rgba(249, 250, 251, 0.8)', 'rgba(30, 30, 30, 0.9)'),
                  border: getColor('1px solid rgba(229, 231, 235, 0.5)', '1px solid rgba(60, 60, 60, 0.6)'),
                  borderRadius: '16px',
                  padding: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = `0 12px 32px ${action.color}25`;
                  e.currentTarget.style.borderColor = action.color;
                  e.currentTarget.style.background = `${action.color}08`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = isDarkMode ? 'rgba(60, 60, 60, 0.6)' : 'rgba(229, 231, 235, 0.5)';
                  e.currentTarget.style.background = isDarkMode ? 'rgba(30, 30, 30, 0.9)' : 'rgba(249, 250, 251, 0.8)';
                }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: action.gradient,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.875rem',
                  boxShadow: `0 4px 16px ${action.color}40`
                }}>
                  <FontAwesomeIcon icon={action.icon} style={{ fontSize: '24px', color: 'white' }} />
                </div>
                <span style={{
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: getColor('#1F2937', '#F9FAFB'),
                  display: 'block'
                }}>
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Orders - Enhanced with Modern Design */}
        <div style={{
          background: getColor('rgba(255, 255, 255, 0.9)', 'rgba(18, 18, 18, 0.98)'),
          backdropFilter: 'blur(20px)',
          border: getColor('1px solid rgba(209, 213, 219, 0.3)', '1px solid rgba(50, 50, 50, 0.6)'),
          borderRadius: '20px',
          padding: '1.75rem',
          boxShadow: getColor('0 4px 12px rgba(0, 0, 0, 0.05)', '0 4px 12px rgba(0, 0, 0, 0.6)')
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FontAwesomeIcon icon={faShoppingCart} style={{ color: 'white', fontSize: '18px' }} />
              </div>
              <h2 style={{ fontSize: '1.35rem', fontWeight: '700', color: getColor('#1F2937', '#F9FAFB'), margin: 0 }}>
                Recent Orders
              </h2>
            </div>
            <button
              onClick={() => setActiveSection('orders')}
              style={{
                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '0.625rem 1.25rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.25)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 16px rgba(34, 197, 94, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 2px 8px rgba(34, 197, 94, 0.25)';
              }}
            >
              View All
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {orders.length > 0 ? (
              orders.slice(0, 5).map(order => (
                <div
                  key={order.id}
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    background: getColor('rgba(249, 250, 251, 0.8)', 'rgba(30, 30, 30, 0.9)'),
                    borderRadius: '16px',
                    padding: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: getColor('1px solid rgba(229, 231, 235, 0.5)', '1px solid rgba(60, 60, 60, 0.6)'),
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(4px)';
                    e.currentTarget.style.boxShadow = isDarkMode ? '0 8px 24px rgba(0, 0, 0, 0.4)' : '0 8px 24px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.borderColor = '#22C55E';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = isDarkMode ? 'rgba(60, 60, 60, 0.6)' : 'rgba(229, 231, 235, 0.5)';
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      fontSize: '1.05rem',
                      fontWeight: '600',
                      color: getColor('#1F2937', '#F9FAFB'),
                      marginBottom: '0.375rem',
                      lineHeight: '1.4'
                    }}>
                      {order.product}
                    </h4>
                    <p style={{
                      fontSize: '0.875rem',
                      color: getColor('#6B7280', '#9CA3AF'),
                      marginBottom: '0.5rem'
                    }}>
                      {order.customer}
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                    <span style={{
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {formatCurrency(order.amount)}
                    </span>
                    <span style={{
                      fontSize: '0.8rem',
                      padding: '0.375rem 0.625rem',
                      borderRadius: '8px',
                      fontWeight: '600',
                      background: getColor('rgba(34, 197, 94, 0.1)', 'rgba(34, 197, 94, 0.15)'),
                      color: '#22C55E'
                    }}>
                      {getStatusText(order.status)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: getColor(
                    'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                    'linear-gradient(135deg, #374151 0%, #4B5563 100%)'
                  ),
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <FontAwesomeIcon icon={faShoppingCart} style={{ fontSize: '32px', color: getColor('#9CA3AF', '#6B7280') }} />
                </div>
                <p style={{ fontSize: '1rem', color: getColor('#6B7280', '#9CA3AF'), fontWeight: '500', marginBottom: '0.5rem' }}>
                  No orders yet
                </p>
                <p style={{ fontSize: '0.875rem', color: getColor('#9CA3AF', '#6B7280') }}>
                  Your recent orders will appear here
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sales Overview Chart - Full Width Modern Section */}
      <div style={{
        background: getColor('rgba(255, 255, 255, 0.9)', 'rgba(18, 18, 18, 0.98)'),
        backdropFilter: 'blur(20px)',
        border: getColor('1px solid rgba(209, 213, 219, 0.3)', '1px solid rgba(50, 50, 50, 0.6)'),
        borderRadius: '20px',
        padding: '1.75rem',
        marginTop: '1.5rem',
        boxShadow: getColor('0 4px 12px rgba(0, 0, 0, 0.05)', '0 4px 12px rgba(0, 0, 0, 0.6)')
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FontAwesomeIcon icon={faChartLine} style={{ color: 'white', fontSize: '18px' }} />
          </div>
          <h2 style={{ fontSize: '1.35rem', fontWeight: '700', color: getColor('#1F2937', '#F9FAFB'), margin: 0 }}>
            Sales Overview
          </h2>
        </div>
        <div style={{ minHeight: '300px' }}>
          <Line data={salesChartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );

  const renderAddProductForm = () => (
    <div className="AutoboyExpress-dash-modal-overlay" onClick={() => setShowAddProductForm(false)}>
      <div className="AutoboyExpress-dash-modal" onClick={(e) => e.stopPropagation()}>
        <div className="AutoboyExpress-dash-modal-header">
          <h2>Add New Product</h2>
          <button className="AutoboyExpress-dash-modal-close" onClick={() => setShowAddProductForm(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmitProduct} className="AutoboyExpress-dash-product-form">
          {/* Product Images */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Product Images (Max 10)</h3>
            <div className="AutoboyExpress-dash-image-upload-area">
              <input
                type="file"
                id="product-images"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                disabled={uploadingImages || productImages.length >= 10}
              />
              <label
                htmlFor="product-images"
                className="AutoboyExpress-dash-upload-label"
                style={{
                  opacity: uploadingImages || productImages.length >= 10 ? 0.6 : 1,
                  cursor: uploadingImages || productImages.length >= 10 ? 'not-allowed' : 'pointer'
                }}
              >
                <FontAwesomeIcon icon={uploadingImages ? faSpinner : faUpload} spin={uploadingImages} />
                <span>
                  {uploadingImages
                    ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                    : productImages.length >= 10
                      ? 'Maximum images reached (10/10)'
                      : 'Click to upload images from your device'}
                </span>
                {uploadingImages && uploadProgress.fileName && (
                  <small style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                    {uploadProgress.fileName}
                  </small>
                )}
              </label>
            </div>

            {/* Manual Image URL Input */}
            <div className="AutoboyExpress-dash-form-group" style={{ marginTop: '1rem' }}>
              <label>Or Add Image URL</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  className="AutoboyExpress-dash-input"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={handleAddImageUrl}
                  className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                  style={{ padding: '0.5rem 1rem' }}
                  disabled={productImages.length >= 10}
                >
                  Add URL
                </button>
              </div>
              <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                Use direct image URLs from Imgur, Cloudinary, or any image hosting service
              </small>
            </div>

            {productImages.length > 0 && (
              <div className="AutoboyExpress-dash-image-preview-grid">
                {productImages.map((img, index) => (
                  <div key={index} className="AutoboyExpress-dash-image-preview">
                    <img src={img} alt={`Product ${index + 1}`} />
                    <button
                      type="button"
                      className="AutoboyExpress-dash-remove-image"
                      onClick={() => removeImage(index)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Basic Information */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Basic Information</h3>
            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Product Title *</label>
                <input
                  type="text"
                  name="title"
                  value={newProduct.title}
                  onChange={handleProductInputChange}
                  placeholder="e.g., iPhone 15 Pro Max 256GB"
                  required
                />
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={newProduct.category}
                  onChange={handleProductInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.length > 0 ? (
                    categories.map(cat => (
                      <option key={cat._id || cat.id} value={cat._id || cat.id}>
                        {cat.name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="smartphones">Smartphones</option>
                      <option value="laptops">Laptops</option>
                      <option value="tablets">Tablets</option>
                      <option value="smartwatches">Smartwatches</option>
                      <option value="headphones">Headphones</option>
                      <option value="gaming_consoles">Gaming Consoles</option>
                      <option value="cameras">Cameras</option>
                      <option value="audio_equipment">Audio Equipment</option>
                      <option value="accessories">Accessories</option>
                    </>
                  )}
                </select>
                {categories.length === 0 && (
                  <small style={{ color: '#ff6b6b', fontSize: '0.85rem' }}>
                    Loading categories... If categories don't load, contact support.
                  </small>
                )}
                {newProduct.category === 'accessories' && JSON.parse(localStorage.getItem('user') || '{}').user_type !== 'admin' && (
                  <div style={{
                    color: '#ef4444', fontSize: '0.85rem', marginTop: '4px',
                    padding: '8px 12px', background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.2)'
                  }}>
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }} />
                    Only admins can post in the Accessories category. Please select a different category.
                  </div>
                )}
              </div>

              <div className="AutoboyExpress-dash-form-group">
                <label>Condition *</label>
                <select
                  name="condition"
                  value={newProduct.condition}
                  onChange={handleProductInputChange}
                  required
                >
                  <option value="new">Brand New</option>
                  <option value="uk_used">UK Used</option>
                  <option value="nigeria_used">Nigerian Used</option>
                  <option value="swap_deal">Swap Deal</option>
                  <option value="for_sale">For Sale</option>
                </select>
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Brand</label>
                <input
                  type="text"
                  name="brand"
                  value={newProduct.brand}
                  onChange={handleProductInputChange}
                  placeholder="e.g., Apple, Samsung"
                />
              </div>

              <div className="AutoboyExpress-dash-form-group">
                <label>Model</label>
                <input
                  type="text"
                  name="model"
                  value={newProduct.model}
                  onChange={handleProductInputChange}
                  placeholder="e.g., iPhone 15 Pro Max"
                />
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Colours available</label>
                <ColorMultiSelect
                  value={newProduct.colors}
                  onChange={(colors) => setNewProduct(prev => ({ ...prev, colors, color: colors[0] || '' }))}
                  availableColors={availableColors}
                  loading={loadingColors}
                  modelEntered={!!newProduct.model}
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>
          </div>

          {/* Pricing & Stock */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Pricing & Stock</h3>
            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Price (₦) *</label>
                <input
                  type="number"
                  name="price"
                  value={newProduct.price}
                  onChange={handleProductInputChange}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  required
                />
                <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                  Regular price shown to buyers
                </small>
              </div>

              <div className="AutoboyExpress-dash-form-group">
                <label>Seller Price (₦) - Optional</label>
                <input
                  type="number"
                  name="sellerPrice"
                  value={newProduct.sellerPrice}
                  onChange={handleProductInputChange}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
                <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                  Wholesale price for other sellers (must be less than regular price)
                </small>
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Quantity in Stock *</label>
                <input
                  type="number"
                  name="quantity"
                  value={newProduct.quantity}
                  onChange={handleProductInputChange}
                  placeholder="0"
                  min="1"
                  required
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Product Description</h3>
            <div className="AutoboyExpress-dash-form-group">
              <label>Description *</label>
              <textarea
                name="description"
                value={newProduct.description}
                onChange={handleProductInputChange}
                rows="5"
                placeholder="Describe your product in detail..."
                required
              />
            </div>

            <div className="AutoboyExpress-dash-form-group">
              <label>Specifications</label>
              <textarea
                name="specifications"
                value={newProduct.specifications}
                onChange={handleProductInputChange}
                rows="4"
                placeholder="Storage: 256GB, RAM: 8GB, Color: Space Black..."
              />
            </div>
          </div>

          {/* Location & Additional */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Location & Additional Info</h3>
            <div className="AutoboyExpress-dash-form-group">
              <label>Location *</label>
              <input
                type="text"
                name="location"
                value={newProduct.location}
                onChange={handleProductInputChange}
                placeholder="e.g., Lagos, Ikeja"
                required
              />
            </div>

            <div className="AutoboyExpress-dash-form-group">
              <label>Tags (comma separated)</label>
              <input
                type="text"
                name="tags"
                value={newProduct.tags}
                onChange={handleProductInputChange}
                placeholder="e.g., smartphone, apple, ios"
              />
            </div>

            <div className="AutoboyExpress-dash-form-group">
              <label className="AutoboyExpress-dash-checkbox-label">
                <input
                  type="checkbox"
                  name="swapAvailable"
                  checked={newProduct.swapAvailable}
                  onChange={handleProductInputChange}
                />
                <span>Available for Swap/Exchange</span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="AutoboyExpress-dash-form-actions">
            <button
              type="button"
              className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
              onClick={() => setShowAddProductForm(false)}
            >
              Cancel
            </button>
            <button type="submit" className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary">
              <FontAwesomeIcon icon={faSave} />
              Add Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderProductsContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">My Products</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* View Toggle */}
            <div className="AutoboyExpress-view-toggle">
              <button
                className={`AutoboyExpress-view-btn ${productsViewMode === 'list' ? 'active' : ''}`}
                onClick={() => setProductsViewMode('list')}
                title="List View"
              >
                <FontAwesomeIcon icon={faList} />
              </button>
              <button
                className={`AutoboyExpress-view-btn ${productsViewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setProductsViewMode('grid')}
                title="Grid View"
              >
                <FontAwesomeIcon icon={faThLarge} />
              </button>
            </div>
            <button
              className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
              onClick={() => setShowAddProductForm(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Product
            </button>
          </div>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          {productsViewMode === 'list' ? (
            // List View (Modern Cards)
            <div className="AutoboyExpress-modern-list-container">
              {products.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF' }}>
                  No products yet. Click "Add Product" to get started.
                </div>
              ) : products.map(product => (
                <div key={product.id} className="AutoboyExpress-modern-list-item">
                  {/* Product Image Section */}
                  <div className="AutoboyExpress-list-image-section">
                    <div className="AutoboyExpress-list-image-wrapper">
                      <img
                        src={product.image || 'https://via.placeholder.com/120x120?text=No+Image'}
                        alt={product.name}
                        className="AutoboyExpress-list-image"
                      />
                    </div>
                    <div className="AutoboyExpress-list-status-badge" data-status={product.status}>
                      <span className="AutoboyExpress-status-dot"></span>
                      {getStatusText(product.status)}
                    </div>
                  </div>

                  {/* Product Info Section */}
                  <div className="AutoboyExpress-list-info-section">
                    <h3 className="AutoboyExpress-list-product-name">{product.name}</h3>
                    <div className="AutoboyExpress-list-product-price">{formatCurrency(product.price)}</div>
                  </div>

                  {/* Metrics Section */}
                  <div className="AutoboyExpress-list-metrics-section">
                    <div className="AutoboyExpress-list-metric">
                      <span className="AutoboyExpress-list-metric-label">Stock</span>
                      <span className="AutoboyExpress-list-metric-value">{product.stock || 0}</span>
                    </div>
                    <div className="AutoboyExpress-list-metric">
                      <span className="AutoboyExpress-list-metric-label">Sales</span>
                      <span className="AutoboyExpress-list-metric-value">{product.sold || 0}</span>
                    </div>
                    <div className="AutoboyExpress-list-metric">
                      <span className="AutoboyExpress-list-metric-label">Revenue</span>
                      <span className="AutoboyExpress-list-metric-value">
                        ₦{(((product.price || 0) * (product.sold || 0)) / 1000).toFixed(0)}k
                      </span>
                    </div>
                  </div>

                  {/* Actions Section */}
                  <div className="AutoboyExpress-list-actions-section">
                    <button
                      className="AutoboyExpress-list-action-btn AutoboyExpress-list-btn-view"
                      onClick={() => handleViewProduct(product)}
                      title="View Details"
                    >
                      <FontAwesomeIcon icon={faEye} />
                      <span>View</span>
                    </button>
                    <button
                      className="AutoboyExpress-list-action-btn AutoboyExpress-list-btn-edit"
                      onClick={() => handleEditProduct(product)}
                      title="Edit Product"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      <span>Edit</span>
                    </button>
                    <button
                      className="AutoboyExpress-list-action-btn AutoboyExpress-list-btn-delete"
                      onClick={() => handleDeleteProduct(product._id || product.id)}
                      title="Delete Product"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Grid View (Modern Cards)
            <div className="AutoboyExpress-dash-products-grid">
              {products.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: '#9CA3AF' }}>
                  No products yet. Click "Add Product" to get started.
                </div>
              ) : products.map(product => (
                <div key={product.id} className="AutoboyExpress-modern-product-card">
                  {/* Status Badge */}
                  <div className="AutoboyExpress-product-status-badge" data-status={product.status}>
                    <span className="AutoboyExpress-status-dot"></span>
                    {getStatusText(product.status)}
                  </div>

                  {/* Product Image */}
                  <div className="AutoboyExpress-product-image-wrapper">
                    <img
                      src={product.image || 'https://via.placeholder.com/300x300?text=No+Image'}
                      alt={product.name}
                      className="AutoboyExpress-product-image"
                    />
                    <div className="AutoboyExpress-product-overlay">
                      <button
                        className="AutoboyExpress-product-quick-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewProduct(product);
                        }}
                        title="Quick View"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="AutoboyExpress-product-info">
                    <h3 className="AutoboyExpress-product-name">{product.name}</h3>

                    {/* Price Section */}
                    <div className="AutoboyExpress-product-price-section">
                      <div className="AutoboyExpress-product-price">
                        <span className="AutoboyExpress-price-label">Price</span>
                        <span className="AutoboyExpress-price-value">{formatCurrency(product.price)}</span>
                      </div>
                    </div>

                    {/* Metrics */}
                    <div className="AutoboyExpress-product-metrics">
                      <div className="AutoboyExpress-metric">
                        <span className="AutoboyExpress-metric-label">Stock</span>
                        <span className="AutoboyExpress-metric-value">{product.stock || 0}</span>
                      </div>
                      <div className="AutoboyExpress-metric-divider"></div>
                      <div className="AutoboyExpress-metric">
                        <span className="AutoboyExpress-metric-label">Sales</span>
                        <span className="AutoboyExpress-metric-value">{product.sold || 0}</span>
                      </div>
                      <div className="AutoboyExpress-metric-divider"></div>
                      <div className="AutoboyExpress-metric">
                        <span className="AutoboyExpress-metric-label">Revenue</span>
                        <span className="AutoboyExpress-metric-value">
                          ₦{(((product.price || 0) * (product.sold || 0)) / 1000).toFixed(0)}k
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="AutoboyExpress-product-actions">
                      <button
                        className="AutoboyExpress-action-btn AutoboyExpress-btn-edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditProduct(product);
                        }}
                        title="Edit Product"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                        <span>Edit</span>
                      </button>
                      <button
                        className="AutoboyExpress-action-btn AutoboyExpress-btn-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProduct(product._id || product.id);
                        }}
                        title="Delete Product"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Filter and paginate orders
  const getFilteredOrders = () => {
    let filtered = orders;

    // Apply status filter
    if (ordersFilter !== 'all') {
      filtered = filtered.filter(order => order.status === ordersFilter);
    }

    // Apply search filter
    if (ordersSearchQuery.trim()) {
      const query = ordersSearchQuery.toLowerCase();
      filtered = filtered.filter(order =>
        order.customer?.toLowerCase().includes(query) ||
        order.order_number?.toLowerCase().includes(query) ||
        order.product?.toLowerCase().includes(query)
      );
    }

    // Newest first. The backend now sorts too, but this keeps the table
    // correct regardless of what any given source returns. Copy before
    // sorting — `filtered` can still be the `orders` array itself when no
    // filter is active, and sorting in place would mutate state.
    return [...filtered].sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const getPaginatedOrders = () => {
    const filtered = getFilteredOrders();
    const indexOfLastOrder = ordersCurrentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
    return filtered.slice(indexOfFirstOrder, indexOfLastOrder);
  };

  const totalFilteredOrders = getFilteredOrders().length;
  const totalPages = Math.ceil(totalFilteredOrders / ordersPerPage);

  const renderOrdersContent = () => {
    const paginatedOrders = getPaginatedOrders();
    const filteredOrders = getFilteredOrders();

    // Shared between the table row's Actions cell and the mobile/tablet
    // card's action row — one definition, so the two views can't drift.
    const renderOrderActions = (order) => (
      <>
        <IconBtn title="View details" onClick={() => navigate(`/order-confirmation/${order._id || order.id}`)}>
          <FontAwesomeIcon icon={faEye} />
        </IconBtn>
        {/* IMEI entry — only once the order is actually paid for; nothing to
            record while pending. compact renders it icon-sized (matching the
            buttons around it) instead of the full text pill, which used to
            be wider than the whole Actions column by itself. */}
        {order.status !== 'pending' && order.status !== 'cancelled' && (
          <OrderIMEIButton order={order} showCustomToast={showCustomToast} isDarkMode={isDarkMode} compact />
        )}
        {/* Ship — pending/processing orders */}
        {(order.status === 'pending' || order.status === 'processing') && (
          <IconBtn
            title="Mark as shipped"
            disabled={processingOrderAction === `${order.id}-ship`}
            onClick={() => handleShipOrder(order._id || order.id)}
          >
            {processingOrderAction === `${order.id}-ship` ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTruck} />}
          </IconBtn>
        )}
        {/* Delivered — shipped orders */}
        {order.status === 'shipped' && (
          <IconBtn
            tone="green"
            title="Mark as delivered"
            disabled={processingOrderAction === `${order.id}-deliver`}
            onClick={() => handleMarkDelivered(order._id || order.id)}
          >
            {processingOrderAction === `${order.id}-deliver` ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />}
          </IconBtn>
        )}
        {/* Reject — pending/processing orders */}
        {(order.status === 'pending' || order.status === 'processing') && (
          <IconBtn
            tone="red"
            title="Reject order"
            disabled={processingOrderAction === `${order.id}-reject`}
            onClick={() => handleRejectOrder(order._id || order.id)}
          >
            {processingOrderAction === `${order.id}-reject` ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faBan} />}
          </IconBtn>
        )}
        {order.use_escrow && (
          <FontAwesomeIcon icon={faShieldAlt} className="tw-text-green-500 tw-self-center" title="Protected by escrow" />
        )}
      </>
    );

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {/* Order Stats Overview */}
        <div className="AutoboyExpress-premium-orders-stats">
          <div className="AutoboyExpress-premium-order-stat-card">
            <div className="AutoboyExpress-premium-order-stat-icon" style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}>
              <FontAwesomeIcon icon={faClipboardList} />
            </div>
            <div className="AutoboyExpress-premium-order-stat-content">
              <h3>All Orders</h3>
              <p className="AutoboyExpress-premium-order-stat-value">{orders.length}</p>
            </div>
          </div>
          <div className="AutoboyExpress-premium-order-stat-card">
            <div className="AutoboyExpress-premium-order-stat-icon" style={{ background: 'linear-gradient(135deg, #3B82F6, #2563EB)' }}>
              <FontAwesomeIcon icon={faSpinner} />
            </div>
            <div className="AutoboyExpress-premium-order-stat-content">
              <h3>Pending</h3>
              <p className="AutoboyExpress-premium-order-stat-value">{orders.filter(o => o.status === 'pending').length}</p>
            </div>
          </div>
          <div className="AutoboyExpress-premium-order-stat-card">
            <div className="AutoboyExpress-premium-order-stat-icon" style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}>
              <FontAwesomeIcon icon={faTruck} />
            </div>
            <div className="AutoboyExpress-premium-order-stat-content">
              <h3>Shipped</h3>
              <p className="AutoboyExpress-premium-order-stat-value">{orders.filter(o => o.status === 'shipped').length}</p>
            </div>
          </div>
          <div className="AutoboyExpress-premium-order-stat-card">
            <div className="AutoboyExpress-premium-order-stat-icon" style={{ background: 'linear-gradient(135deg, #22C55E, #10B981)' }}>
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className="AutoboyExpress-premium-order-stat-content">
              <h3>Completed</h3>
              <p className="AutoboyExpress-premium-order-stat-value">{orders.filter(o => o.status === 'delivered' || o.status === 'completed').length}</p>
            </div>
          </div>
        </div>

        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">
              <FontAwesomeIcon icon={faShoppingCart} style={{ marginRight: '0.5rem' }} />
              Order Management
            </h2>
            <div className="AutoboyExpress-premium-orders-controls">
              <div className="AutoboyExpress-premium-filter-group">
                <select
                  className="AutoboyExpress-premium-filter-select"
                  value={ordersFilter}
                  onChange={(e) => {
                    setOrdersFilter(e.target.value);
                    setOrdersCurrentPage(1);
                  }}
                >
                  <option value="all">All Orders</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="AutoboyExpress-dash-search">
                <FontAwesomeIcon icon={faSearch} />
                <input
                  type="text"
                  placeholder="Search by customer, order ID..."
                  value={ordersSearchQuery}
                  onChange={(e) => {
                    setOrdersSearchQuery(e.target.value);
                    setOrdersCurrentPage(1);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            {paginatedOrders.length === 0 ? (
              <div className="AutoboyExpress-premium-empty-state">
                <FontAwesomeIcon icon={faShoppingCart} size="3x" />
                <h3>No orders found</h3>
                <p>{ordersFilter !== 'all' || ordersSearchQuery ? 'Try adjusting your filters' : 'Orders will appear here once customers make purchases'}</p>
              </div>
            ) : (
              <>
                {/* Table — small-PC and up. */}
                <div className="tw-hidden lg:tw-block">
                  <TableCard>
                    <DataTable
                      // Order ID falls back to the raw Mongo id (24 unbroken
                      // hex characters) whenever order_number isn't set — at
                      // the old 11% it regularly ran past its own column into
                      // Customer, since a string with no spaces has nowhere
                      // to wrap and (correctly, post the Td overflow-hidden
                      // fix) is no longer silently clipped either. Actions
                      // gave the room: every action in it is now a fixed
                      // 32px icon rather than the old text pill.
                      columns={[{ width: '16%' }, { width: '17%' }, { width: '14%' }, { width: '10%' }, { width: '9%' }, { width: '10%' }, { width: '9%' }, { width: '15%' }]}
                      minWidth="1150px"
                    >
                      <THead>
                        <Th>Order ID</Th>
                        <Th>Customer</Th>
                        <Th>Products</Th>
                        <Th align="right">Amount</Th>
                        <Th>Date</Th>
                        <Th>Order Status</Th>
                        <Th>Payment</Th>
                        <Th align="center">Actions</Th>
                      </THead>
                      <tbody>
                        {paginatedOrders.map(order => (
                          <Tr key={order.id}>
                            <Td className="tw-font-semibold tw-truncate" title={`${order.order_number || order.id}`}>#{order.order_number || order.id}</Td>
                            <Td>
                              <div className="tw-flex tw-items-center tw-gap-2.5 tw-min-w-0">
                                <span className="tw-w-8 tw-h-8 tw-shrink-0 tw-rounded-full tw-bg-green-500/15 tw-text-green-600 dark:tw-text-green-400 tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-bold">
                                  {(order.customer || 'Customer').charAt(0).toUpperCase()}
                                </span>
                                <div className="tw-flex tw-flex-col tw-min-w-0">
                                  <span className="tw-font-medium tw-truncate tw-flex tw-items-center tw-gap-1.5" title={order.customer || 'Customer'}>
                                    {order.customer || 'Customer'}
                                    {order.special_packaging && (
                                      <span title="Premium buyer — use premium packaging" className="tw-text-[0.65rem] tw-font-bold tw-text-amber-600 dark:tw-text-amber-400 tw-bg-amber-500/10 tw-border tw-border-amber-500/25 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-inline-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
                                        <FontAwesomeIcon icon={faGift} /> Premium Packaging
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="tw-flex tw-flex-col tw-min-w-0">
                                <span className="tw-truncate" title={order.product}>{order.product}</span>
                                <span className="tw-text-xs tw-text-zinc-500 dark:tw-text-zinc-400">Qty: {order.quantity || 1}</span>
                              </div>
                            </Td>
                            <Td align="right" className="tw-font-semibold tw-tabular-nums">
                              {formatCurrency(order.amount)}
                              {typeof order.sellerPayout === 'number' && (
                                <div className="tw-text-xs tw-font-normal tw-text-green-600 dark:tw-text-green-400 tw-mt-0.5">
                                  You get: {formatCurrency(order.sellerPayout)}
                                </div>
                              )}
                            </Td>
                            <Td className="tw-text-zinc-500 dark:tw-text-zinc-400">{formatDate(order.date)}</Td>
                            <Td><Badge tone={statusTone(order.status)}>{getStatusText(order.status)}</Badge></Td>
                            <Td><Badge tone={statusTone(order.payment_status)}>{getPaymentStatusText(order.payment_status, order)}</Badge></Td>
                            <Td align="center">
                              <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-flex-wrap">
                                {renderOrderActions(order)}
                              </div>
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </DataTable>
                  </TableCard>
                </div>

                {/* Cards — mobile, tablet, and small-PC widths. Same data,
                    stacked, so nothing needs horizontal scrolling to be read. */}
                <div className="lg:tw-hidden tw-flex tw-flex-col tw-gap-3">
                  {paginatedOrders.map(order => (
                    <div
                      key={order.id}
                      className="tw-rounded-2xl tw-border tw-border-black/5 dark:tw-border-white/10 tw-bg-white/95 dark:tw-bg-zinc-900/70 tw-p-4 tw-shadow-[0_2px_10px_rgba(0,0,0,0.04)]"
                    >
                      <div className="tw-flex tw-justify-between tw-items-start tw-gap-3">
                        <div className="tw-min-w-0">
                          <div className="tw-font-semibold tw-text-sm tw-truncate">#{order.order_number || order.id}</div>
                          <div className="tw-text-xs tw-text-zinc-500 dark:tw-text-zinc-400 tw-mt-0.5">{formatDate(order.date)}</div>
                        </div>
                        <div className="tw-text-right tw-flex-shrink-0">
                          <div className="tw-font-bold tw-text-sm tw-tabular-nums">{formatCurrency(order.amount)}</div>
                          {typeof order.sellerPayout === 'number' && (
                            <div className="tw-text-xs tw-font-medium tw-text-green-600 dark:tw-text-green-400 tw-mt-0.5">
                              You get: {formatCurrency(order.sellerPayout)}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="tw-flex tw-items-center tw-gap-2.5 tw-mt-3 tw-min-w-0">
                        <span className="tw-w-8 tw-h-8 tw-shrink-0 tw-rounded-full tw-bg-green-500/15 tw-text-green-600 dark:tw-text-green-400 tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-bold">
                          {(order.customer || 'Customer').charAt(0).toUpperCase()}
                        </span>
                        <div className="tw-min-w-0">
                          <div className="tw-flex tw-items-center tw-gap-1.5 tw-font-medium tw-text-sm tw-truncate">
                            <span className="tw-truncate">{order.customer || 'Customer'}</span>
                            {order.special_packaging && (
                              <span title="Premium buyer — use premium packaging" className="tw-text-[0.65rem] tw-font-bold tw-text-amber-600 dark:tw-text-amber-400 tw-bg-amber-500/10 tw-border tw-border-amber-500/25 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-inline-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
                                <FontAwesomeIcon icon={faGift} /> Premium
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="tw-text-sm tw-text-zinc-600 dark:tw-text-zinc-300 tw-mt-2 tw-break-words">
                        {order.product} <span className="tw-text-zinc-400 dark:tw-text-zinc-500">· Qty {order.quantity || 1}</span>
                      </div>

                      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mt-3">
                        <Badge tone={statusTone(order.status)}>{getStatusText(order.status)}</Badge>
                        <Badge tone={statusTone(order.payment_status)}>{getPaymentStatusText(order.payment_status, order)}</Badge>
                        {order.use_escrow && (
                          <FontAwesomeIcon icon={faShieldAlt} className="tw-text-green-500" title="Protected by escrow" />
                        )}
                      </div>

                      <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-1.5 tw-mt-3 tw-pt-3 tw-border-t tw-border-black/5 dark:tw-border-white/10">
                        {renderOrderActions(order)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                <div className="AutoboyExpress-premium-pagination">
                  <div className="AutoboyExpress-premium-pagination-info">
                    Showing {((ordersCurrentPage - 1) * ordersPerPage) + 1} to {Math.min(ordersCurrentPage * ordersPerPage, totalFilteredOrders)} of {totalFilteredOrders} orders
                  </div>
                  <div className="AutoboyExpress-premium-pagination-controls">
                    <button
                      className="AutoboyExpress-premium-pagination-btn"
                      onClick={() => setOrdersCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={ordersCurrentPage === 1}
                    >
                      <FontAwesomeIcon icon={faChartBar} rotation={180} /> Previous
                    </button>
                    <div className="AutoboyExpress-premium-pagination-pages">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          className={`AutoboyExpress-premium-page-btn ${page === ordersCurrentPage ? 'active' : ''}`}
                          onClick={() => setOrdersCurrentPage(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button
                      className="AutoboyExpress-premium-pagination-btn"
                      onClick={() => setOrdersCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={ordersCurrentPage === totalPages}
                    >
                      Next <FontAwesomeIcon icon={faChartBar} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEarningsContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1', marginBottom: '2rem' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">Wallet & Earnings</h2>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => setShowWithdrawModal(true)}
            disabled={!walletBalance?.balance || walletBalance.balance <= 0}
          >
            <FontAwesomeIcon icon={faWallet} /> Request Withdrawal
          </button>
        </div>
      </div>
      <div className="AutoboyExpress-dash-earnings-cards">
        <div className="AutoboyExpress-dash-earnings-card">
          <div className="AutoboyExpress-dash-earnings-icon">
            <FontAwesomeIcon icon={faWallet} />
          </div>
          <div className="AutoboyExpress-dash-earnings-info">
            <h3>Available Balance</h3>
            <div className="AutoboyExpress-dash-earnings-amount">
              {/* walletData is only ever set once, at initial page load — the
                  30s refresh (fetchWalletBalance) only updates walletBalance,
                  so a seller parked on this tab saw a frozen balance that
                  didn't reflect a topup/payout that had already landed on
                  the Wallet tab in the same session. */}
              {formatCurrency(walletBalance?.balance || 0)}
            </div>
          </div>
        </div>
        <div className="AutoboyExpress-dash-earnings-card">
          <div className="AutoboyExpress-dash-earnings-icon">
            <FontAwesomeIcon icon={faClipboardList} />
          </div>
          <div className="AutoboyExpress-dash-earnings-info">
            <h3>Held in Escrow</h3>
            <div className="AutoboyExpress-dash-earnings-amount">
              {formatCurrency(walletBalance?.heldBalance || 0)}
            </div>
          </div>
        </div>
        <div className="AutoboyExpress-dash-earnings-card">
          <div className="AutoboyExpress-dash-earnings-icon">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div className="AutoboyExpress-dash-earnings-info">
            <h3>This Month</h3>
            <div className="AutoboyExpress-dash-earnings-amount">
              {formatCurrency(stats.totalEarnings || 0)}
            </div>
          </div>
        </div>
      </div>

      <div className="AutoboyExpress-dash-content">
        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Monthly Earnings</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-dash-chart-container">
              <Bar data={monthlyEarningsData} options={chartOptions} />
            </div>
          </div>
        </div>

        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Earnings Breakdown</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-dash-earnings-breakdown">
              <div className="AutoboyExpress-dash-breakdown-item">
                <span>Product Sales</span>
                <span>{formatCurrency(2100000)}</span>
              </div>
              <div className="AutoboyExpress-dash-breakdown-item">
                <span>Commission</span>
                <span>-{formatCurrency(210000)}</span>
              </div>
              <div className="AutoboyExpress-dash-breakdown-item">
                <span>Transaction Fees</span>
                <span>-{formatCurrency(50000)}</span>
              </div>
              <hr />
              <div className="AutoboyExpress-dash-breakdown-item AutoboyExpress-dash-breakdown-total">
                <span>Net Earnings</span>
                <span>{formatCurrency(1840000)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Recent Transactions</h2>
            <button
              className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
              onClick={() => setActiveSection('wallet')}
            >
              View All
            </button>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            {isLoadingWallet ? (
              <p>Loading transactions...</p>
            ) : walletTransactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                <FontAwesomeIcon icon={faWallet} size="3x" style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>No transactions yet</p>
              </div>
            ) : (
              <TableCard>
                <DataTable
                  columns={[{ width: '16%' }, { width: '14%' }, { width: '38%' }, { width: '18%' }, { width: '14%' }]}
                  minWidth="680px"
                >
                  <THead>
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Description</Th>
                    <Th align="right">Amount</Th>
                    <Th>Status</Th>
                  </THead>
                  <tbody>
                    {walletTransactions.slice(0, 5).map((transaction, index) => {
                      const isCredit = ['credit', 'topup', 'refund'].includes(transaction.type);
                      return (
                        <Tr key={transaction.id || index}>
                          <Td className="tw-text-zinc-500 dark:tw-text-zinc-400">
                            {formatDate(transaction.created_at || transaction.date)}
                          </Td>
                          <Td><Badge tone={isCredit ? 'green' : 'amber'}>{transaction.type || 'N/A'}</Badge></Td>
                          <Td title={transaction.description || 'Transaction'}>
                            {transaction.description || 'Transaction'}
                          </Td>
                          <Td
                            align="right"
                            className={`tw-font-bold tw-tabular-nums ${isCredit ? 'tw-text-green-600 dark:tw-text-green-400' : 'tw-text-red-500'}`}
                          >
                            {isCredit ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount || 0))}
                          </Td>
                          <Td>
                            <Badge tone={statusTone(transaction.status || 'pending')}>
                              {transaction.status || 'pending'}
                            </Badge>
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </DataTable>
              </TableCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalyticsContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-content">
        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Sales Trend</h2>
            <p style={{
              fontSize: '0.875rem',
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              margin: '0.25rem 0 0 0'
            }}>
              Daily sales performance over time
            </p>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-dash-chart-container">
              <Line data={salesChartData} options={chartOptions} />
            </div>
          </div>
        </div>

        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Product Categories</h2>
            <p style={{
              fontSize: '0.875rem',
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              margin: '0.25rem 0 0 0'
            }}>
              Sales distribution by category
            </p>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-dash-chart-container">
              <Doughnut data={categoryChartData} options={doughnutOptions} />
            </div>
          </div>
        </div>

        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Performance Metrics</h2>
            <p style={{
              fontSize: '0.875rem',
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              margin: '0.25rem 0 0 0'
            }}>
              Key performance indicators for your store
            </p>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-dash-metrics-grid">
              <div className="AutoboyExpress-dash-metric">
                <h4>Conversion Rate</h4>
                <div className="AutoboyExpress-dash-metric-value">
                  {enhancedStats.conversionRate ? enhancedStats.conversionRate.toFixed(1) : '0.0'}%
                </div>
                <p style={{
                  fontSize: '0.75rem',
                  color: isDarkMode ? '#6B7280' : '#9CA3AF',
                  marginTop: '0.5rem'
                }}>
                  Orders completed vs. total
                </p>
              </div>
              <div className="AutoboyExpress-dash-metric">
                <h4>Average Order Value</h4>
                <div className="AutoboyExpress-dash-metric-value">
                  {formatCurrency(enhancedStats.averageOrderValue || 0)}
                </div>
                <p style={{
                  fontSize: '0.75rem',
                  color: isDarkMode ? '#6B7280' : '#9CA3AF',
                  marginTop: '0.5rem'
                }}>
                  Average revenue per order
                </p>
              </div>
              <div className="AutoboyExpress-dash-metric">
                <h4>Return Rate</h4>
                <div className="AutoboyExpress-dash-metric-value">
                  {enhancedStats.returnRate ? enhancedStats.returnRate.toFixed(1) : '0.0'}%
                </div>
                <p style={{
                  fontSize: '0.75rem',
                  color: isDarkMode ? '#6B7280' : '#9CA3AF',
                  marginTop: '0.5rem'
                }}>
                  Refunded/cancelled orders
                </p>
              </div>
              <div className="AutoboyExpress-dash-metric">
                <h4>Customer Satisfaction</h4>
                <div className="AutoboyExpress-dash-metric-value">
                  {enhancedStats.customerSatisfaction ? enhancedStats.customerSatisfaction.toFixed(1) : '4.5'}/5
                </div>
                <p style={{
                  fontSize: '0.75rem',
                  color: isDarkMode ? '#6B7280' : '#9CA3AF',
                  marginTop: '0.5rem'
                }}>
                  Average customer rating
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Earnings Chart */}
        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Monthly Earnings</h2>
            <p style={{
              fontSize: '0.875rem',
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              margin: '0.25rem 0 0 0'
            }}>
              Revenue trends over the past 6 months
            </p>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-dash-chart-container">
              <Bar data={monthlyEarningsData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettingsContent = () => {
    const tabs = [
      { id: 'profile', label: 'Profile', icon: faUser },
      { id: 'security', label: 'Security', icon: faLock },
      { id: 'notifications', label: 'Notifications', icon: faBell },
      { id: 'sessions', label: 'Sessions', icon: faShieldAlt },
      { id: 'account', label: 'Account', icon: faTrashAlt },
    ];
    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div className="bd-settings-layout">
          <nav className="bd-settings-nav">
            {tabs.map(item => (
              <button
                key={item.id}
                className={`bd-settings-nav-item${settingsTab === item.id ? ' active' : ''}`}
                onClick={() => setSettingsTab(item.id)}
              >
                <FontAwesomeIcon icon={item.icon} className="bd-settings-nav-icon" />
                {item.label}
              </button>
            ))}
          </nav>
          <div className="bd-settings-panel">
            {settingsTab === 'profile' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faUser} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Store Profile</h3>
                    <p className="bd-settings-card-sub">Update your personal and business information</p>
                  </div>
                </div>
                <div className="bd-settings-avatar-row">
                  <div className="bd-settings-avatar-wrap">
                    <img
                      className="bd-settings-avatar-img"
                      src={avatarPreview || userProfile?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.firstName || 'S')}+${encodeURIComponent(userProfile?.lastName || 'eller')}&background=22C55E&color=fff&size=150`}
                      alt="Avatar"
                      onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.firstName || 'S')}+${encodeURIComponent(userProfile?.lastName || 'eller')}&background=22C55E&color=fff&size=150`; }}
                    />
                  </div>
                  <div className="bd-settings-avatar-controls">
                    <div className="bd-settings-avatar-toggle">
                      <button type="button" className={`bd-settings-avatar-toggle-btn${avatarUploadMethod === 'url' ? ' active' : ''}`} onClick={() => setAvatarUploadMethod('url')}>URL</button>
                      <button type="button" className={`bd-settings-avatar-toggle-btn${avatarUploadMethod === 'file' ? ' active' : ''}`} onClick={() => setAvatarUploadMethod('file')}>Upload File</button>
                    </div>
                    {avatarUploadMethod === 'url' ? (
                      <input className="bd-settings-input" type="url" placeholder="https://..." value={userProfile?.avatar || ''} onChange={e => { setUserProfile({ ...userProfile, avatar: e.target.value }); setAvatarPreview(e.target.value); }} />
                    ) : (
                      <>
                        <input ref={avatarFileInputRef} type="file" accept="image/*" onChange={handleAvatarFileSelect} style={{ display: 'none' }} />
                        <button className="bd-settings-btn-outline" type="button" style={{ marginTop: '0.5rem' }} onClick={() => avatarFileInputRef.current?.click()}>
                          <FontAwesomeIcon icon={faUpload} /> {selectedAvatarFile ? selectedAvatarFile.name : 'Choose Image'}
                        </button>
                      </>
                    )}
                    <button className="bd-settings-btn-primary" type="button" style={{ marginTop: '0.75rem' }} onClick={handleAvatarUpload} disabled={uploadingAvatar}>
                      <FontAwesomeIcon icon={uploadingAvatar ? faSpinner : faSave} spin={uploadingAvatar} />
                      {uploadingAvatar ? 'Uploading...' : 'Save Photo'}
                    </button>
                  </div>
                </div>
                <div className="bd-settings-form">
                  <div className="bd-settings-grid-2">
                    <div className="bd-settings-field">
                      <label>First Name</label>
                      <input className="bd-settings-input" type="text" value={settingsFormData.firstName} onChange={e => setSettingsFormData({ ...settingsFormData, firstName: e.target.value })} placeholder="First name" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Last Name</label>
                      <input className="bd-settings-input" type="text" value={settingsFormData.lastName} onChange={e => setSettingsFormData({ ...settingsFormData, lastName: e.target.value })} placeholder="Last name" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Email</label>
                      <input className="bd-settings-input" type="email" value={settingsFormData.email || userProfile?.email || ''} readOnly />
                    </div>
                    <div className="bd-settings-field">
                      <label>Phone</label>
                      <input className="bd-settings-input" type="tel" value={settingsFormData.phone} onChange={e => setSettingsFormData({ ...settingsFormData, phone: e.target.value })} placeholder="+234 XXX XXX XXXX" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Business / Shop Name</label>
                      <input className="bd-settings-input" type="text" value={settingsFormData.businessName} onChange={e => setSettingsFormData({ ...settingsFormData, businessName: e.target.value })} placeholder="Your business or shop name" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Business Phone</label>
                      <input className="bd-settings-input" type="tel" value={settingsFormData.businessPhone} onChange={e => setSettingsFormData({ ...settingsFormData, businessPhone: e.target.value })} placeholder="Business contact number" />
                    </div>
                    <div className="bd-settings-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Business Address</label>
                      <input className="bd-settings-input" type="text" value={settingsFormData.businessAddress} onChange={e => setSettingsFormData({ ...settingsFormData, businessAddress: e.target.value })} placeholder="Business location / address" />
                    </div>
                    <div className="bd-settings-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Bio / Store Description</label>
                      <textarea className="bd-settings-input" rows="3" value={settingsFormData.bio} onChange={e => setSettingsFormData({ ...settingsFormData, bio: e.target.value })} placeholder="Tell customers about your business..." style={{ resize: 'vertical' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
                    <button type="button" className="bd-settings-btn-primary" onClick={handleProfileSave} disabled={isSavingSettings}>
                      <FontAwesomeIcon icon={isSavingSettings ? faSpinner : faSave} spin={isSavingSettings} />
                      {isSavingSettings ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button type="button" className="bd-settings-btn-outline" onClick={() => {
                      const userId = JSON.parse(localStorage.getItem('user') || '{}')._id || JSON.parse(localStorage.getItem('user') || '{}').id;
                      if (userId) window.open(`/store/${userId}`, '_blank');
                      else showCustomToast({ type: 'error', title: 'Error', message: 'Unable to load store profile' });
                    }}>
                      <FontAwesomeIcon icon={faStore} /> View Store
                    </button>
                  </div>
                </div>
              </div>
            )}
            {settingsTab === 'security' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faLock} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Security</h3>
                    <p className="bd-settings-card-sub">Change your password and manage two-factor authentication</p>
                  </div>
                </div>
                <form className="bd-settings-form" onSubmit={handlePasswordChange}>
                  <h4 style={{ marginBottom: '1rem', fontWeight: 600 }}>Change Password</h4>
                  <div className="bd-settings-field">
                    <label>Current Password</label>
                    <input className="bd-settings-input" type="password" value={passwordForm.currentPassword} onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} placeholder="Enter current password" autoComplete="current-password" />
                  </div>
                  <div className="bd-settings-grid-2">
                    <div className="bd-settings-field">
                      <label>New Password</label>
                      <input className="bd-settings-input" type="password" value={passwordForm.newPassword} onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} placeholder="New password" autoComplete="new-password" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Confirm New Password</label>
                      <input className="bd-settings-input" type="password" value={passwordForm.confirmPassword} onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} placeholder="Confirm new password" autoComplete="new-password" />
                    </div>
                  </div>
                  <button type="submit" className="bd-settings-btn-primary" style={{ marginTop: '1rem' }} disabled={savingPassword}>
                    <FontAwesomeIcon icon={savingPassword ? faSpinner : faLock} spin={savingPassword} />
                    {savingPassword ? 'Updating...' : 'Update Password'}
                  </button>
                </form>
                <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid var(--bd-border)' }} />
                <TwoFactorAuth isDarkMode={isDarkMode} />
              </div>
            )}
            {settingsTab === 'notifications' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faBell} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Notifications</h3>
                    <p className="bd-settings-card-sub">Choose how you receive updates and alerts</p>
                  </div>
                </div>
                <div className="bd-notif-list">
                  {[
                    { key: 'emailNotifications', label: 'Email Notifications', sub: 'Receive notifications via email' },
                    { key: 'pushNotifications', label: 'Push Notifications', sub: 'Browser and app push alerts' },
                    { key: 'smsNotifications', label: 'SMS Notifications', sub: 'Text message alerts' },
                    { key: 'orderUpdates', label: 'Order Updates', sub: 'New orders and status changes' },
                    { key: 'priceAlerts', label: 'Price Alerts', sub: 'Notifications about your product pricing' },
                    { key: 'promotions', label: 'Promotions & Marketing', sub: 'Platform deals and campaigns' },
                    { key: 'newMessages', label: 'New Messages', sub: 'Buyer inquiries and chat messages' },
                  ].map(({ key, label, sub }) => (
                    <div key={key} className="bd-notif-item">
                      <div className="bd-notif-item-label">
                        {label}
                        <span className="bd-notif-item-sub">{sub}</span>
                      </div>
                      <label className="bd-toggle">
                        <input type="checkbox" checked={notificationPrefs[key] ?? false} onChange={e => setNotificationPrefs(p => ({ ...p, [key]: e.target.checked }))} />
                        <span className="bd-toggle-track"><span className="bd-toggle-thumb" /></span>
                      </label>
                    </div>
                  ))}
                </div>
                <button className="bd-settings-btn-primary" style={{ marginTop: '1.25rem' }} onClick={handleSaveNotifPrefs} disabled={savingNotifPrefs}>
                  <FontAwesomeIcon icon={savingNotifPrefs ? faSpinner : faSave} spin={savingNotifPrefs} />
                  {savingNotifPrefs ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            )}
            {settingsTab === 'sessions' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faShieldAlt} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Active Sessions</h3>
                    <p className="bd-settings-card-sub">Manage devices logged into your account</p>
                  </div>
                  {activeSessions.length > 1 && (
                    <button className="bd-settings-btn-danger-outline" style={{ marginLeft: 'auto' }} onClick={handleTerminateAllSessions}>
                      Sign Out Other Devices
                    </button>
                  )}
                </div>
                {sessionsLoading ? (
                  <p style={{ color: '#6b7280', padding: '1rem' }}><FontAwesomeIcon icon={faSpinner} spin /> Loading sessions...</p>
                ) : activeSessions.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#9ca3af', padding: '1.5rem' }}>No active sessions found.</p>
                ) : (
                  <div className="bd-session-list">
                    {activeSessions.map((session, idx) => {
                      const sessionId = session.id || session._id || idx;
                      const isCurrent = session.is_current || session.isCurrent;
                      return (
                        <div key={sessionId} className="bd-session-item">
                          <div>
                            <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem' }}>
                              {session.device_name || session.device || session.browser || 'Unknown Device'}
                              {isCurrent && <span className="bd-session-badge">This device</span>}
                            </p>
                            <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                              {session.ip_address || ''}{session.location ? ` · ${session.location}` : ''}
                              {session.last_active ? ` · ${new Date(session.last_active).toLocaleString()}` : ''}
                            </p>
                          </div>
                          {!isCurrent && (
                            <button className="bd-settings-btn-danger-outline" onClick={() => handleTerminateSession(sessionId)} disabled={terminatingSession === sessionId}>
                              {terminatingSession === sessionId ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Sign Out'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {settingsTab === 'account' && (
              <>
                <div className="bd-settings-card">
                  <div className="bd-settings-card-header">
                    <FontAwesomeIcon icon={faDownload} className="bd-settings-card-icon" />
                    <div>
                      <h3 className="bd-settings-card-title">Data Management</h3>
                      <p className="bd-settings-card-sub">Export a copy of your personal data</p>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.9rem', color: 'var(--bd-muted)', marginBottom: '1rem' }}>
                    Request a copy of all your personal data stored on AutoboyExpress. It will be sent to your registered email.
                  </p>
                  <button className="bd-settings-btn-outline" onClick={handleExportData} disabled={exportingData}>
                    {exportingData ? <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</> : <><FontAwesomeIcon icon={faDownload} /> Export My Data</>}
                  </button>
                </div>
                <div className="bd-settings-card bd-settings-card-danger" style={{ marginTop: '1rem' }}>
                  <div className="bd-settings-card-header">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="bd-settings-card-icon" style={{ color: '#ef4444' }} />
                    <div>
                      <h3 className="bd-settings-card-title" style={{ color: '#ef4444' }}>Danger Zone</h3>
                      <p className="bd-settings-card-sub">Permanently delete your account and all data</p>
                    </div>
                  </div>
                  {!showDeleteConfirm ? (
                    <button className="bd-settings-btn-danger" onClick={() => setShowDeleteConfirm(true)}>
                      <FontAwesomeIcon icon={faTrashAlt} /> Delete My Account
                    </button>
                  ) : (
                    <div style={{ background: 'rgba(239,68,68,0.05)', padding: '1.25rem', borderRadius: '10px', border: '1px solid #fecaca' }}>
                      <h4 style={{ color: '#ef4444', marginBottom: '0.75rem' }}>Are you absolutely sure?</h4>
                      <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>Enter your password to confirm account deletion.</p>
                      <form onSubmit={handleDeleteAccount}>
                        <input type="password" className="bd-settings-input" placeholder="Confirm password" value={passwordForDeletion} onChange={e => setPasswordForDeletion(e.target.value)} style={{ marginBottom: '1rem' }} required />
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                          <button type="button" className="bd-settings-btn-outline" onClick={() => { setShowDeleteConfirm(false); setPasswordForDeletion(''); }}>Cancel</button>
                          <button type="submit" className="bd-settings-btn-danger" disabled={deletingAccount || !passwordForDeletion}>
                            {deletingAccount ? <><FontAwesomeIcon icon={faSpinner} spin /> Deleting...</> : 'Yes, Delete Permanently'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderReportsContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      {/* Summary Cards */}
      <div className="AutoboyExpress-premium-report-cards">
        <div className="AutoboyExpress-premium-report-card">
          <div className="AutoboyExpress-premium-report-icon">
            <FontAwesomeIcon icon={faNairaSign} />
          </div>
          <div className="AutoboyExpress-premium-report-info">
            <h3>Total Revenue</h3>
            <div className="AutoboyExpress-premium-report-value">{formatCurrency(reportsData.totalRevenue)}</div>
            {formatGrowth(reportsData.revenueGrowth) && (
              <span className={`AutoboyExpress-premium-report-change ${reportsData.revenueGrowth >= 0 ? 'positive' : 'negative'}`}>
                {formatGrowth(reportsData.revenueGrowth)}
              </span>
            )}
          </div>
        </div>

        <div className="AutoboyExpress-premium-report-card">
          <div className="AutoboyExpress-premium-report-icon">
            <FontAwesomeIcon icon={faShoppingCart} />
          </div>
          <div className="AutoboyExpress-premium-report-info">
            <h3>Total Orders</h3>
            <div className="AutoboyExpress-premium-report-value">{reportsData.totalOrders}</div>
            {formatGrowth(reportsData.ordersGrowth) && (
              <span className={`AutoboyExpress-premium-report-change ${reportsData.ordersGrowth >= 0 ? 'positive' : 'negative'}`}>
                {formatGrowth(reportsData.ordersGrowth)}
              </span>
            )}
          </div>
        </div>

        <div className="AutoboyExpress-premium-report-card">
          <div className="AutoboyExpress-premium-report-icon">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div className="AutoboyExpress-premium-report-info">
            <h3>Avg. Order Value</h3>
            <div className="AutoboyExpress-premium-report-value">{formatCurrency(reportsData.averageOrderValue)}</div>
            {formatGrowth(reportsData.avgOrderGrowth) && (
              <span className={`AutoboyExpress-premium-report-change ${reportsData.avgOrderGrowth >= 0 ? 'positive' : 'negative'}`}>
                {formatGrowth(reportsData.avgOrderGrowth)}
              </span>
            )}
          </div>
        </div>

        <div className="AutoboyExpress-premium-report-card">
          <div className="AutoboyExpress-premium-report-icon">
            <FontAwesomeIcon icon={faChartPie} />
          </div>
          <div className="AutoboyExpress-premium-report-info">
            <h3>Conversion Rate</h3>
            <div className="AutoboyExpress-premium-report-value">{reportsData.conversionRate}%</div>
            {formatGrowth(reportsData.conversionGrowth) && (
              <span className={`AutoboyExpress-premium-report-change ${reportsData.conversionGrowth >= 0 ? 'positive' : 'negative'}`}>
                {formatGrowth(reportsData.conversionGrowth)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Top Products Table */}
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">Top Selling Products</h2>
          <button className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary" onClick={handleDownloadReport}>
            <FontAwesomeIcon icon={faFileAlt} />
            Download Report
          </button>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <TableCard>
            <DataTable columns={[{ width: '56%' }, { width: '20%' }, { width: '24%' }]} minWidth="520px">
              <THead>
                <Th>Product Name</Th>
                <Th align="right">Sales</Th>
                <Th align="right">Revenue</Th>
              </THead>
              <tbody>
                {reportsData.topProducts.length === 0 ? (
                  <TableEmpty colSpan={3} message="No product sales in this period" />
                ) : (
                  reportsData.topProducts.map((product, index) => (
                    <Tr key={index}>
                      <Td className="tw-font-medium" title={product.product || product.name || product.title || product.product_title || 'Product'}>
                        {product.product || product.name || product.title || product.product_title || 'Product'}
                      </Td>
                      <Td align="right" className="tw-tabular-nums">{product.sales}</Td>
                      <Td align="right" className="tw-font-semibold tw-tabular-nums">{formatCurrency(product.revenue)}</Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </TableCard>
        </div>
      </div>
    </div>
  );

  const renderBadgeContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-premium-badge-overview">
        <div className="AutoboyExpress-premium-badge-main">
          <div className="AutoboyExpress-premium-badge-icon">
            <FontAwesomeIcon icon={faCrown} />
          </div>
          <h2>{badgeProgress.currentLevel} Seller</h2>
          <div className="AutoboyExpress-premium-badge-progress">
            <div className="AutoboyExpress-premium-badge-progress-bar">
              <div className="AutoboyExpress-premium-badge-progress-fill" style={{ width: `${(badgeProgress.currentPoints / badgeProgress.nextLevelPoints) * 100}%` }}></div>
            </div>
            <p>{badgeProgress.currentPoints} / {badgeProgress.nextLevelPoints} points to {badgeProgress.nextLevel}</p>
          </div>
        </div>

        <div className="AutoboyExpress-premium-perks-list">
          <h3>Your Seller Perks</h3>
          {badgeProgress.perks.map((perk, idx) => (
            <div key={idx} className="AutoboyExpress-premium-perk-item">
              <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>{perk.icon || '✓'}</span>
              <div>
                <strong>{perk.title || perk}</strong>
                {perk.description && <p style={{ fontSize: '0.85rem', opacity: 0.8, margin: '0.2rem 0 0 0' }}>{perk.description}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="AutoboyExpress-dash-section" style={{ marginTop: '2rem' }}>
        <div className="AutoboyExpress-dash-section-header"><h2 className="AutoboyExpress-dash-section-title">Achievements</h2></div>
        <div className="AutoboyExpress-dash-section-content">
          <div className="AutoboyExpress-premium-achievements-grid">
            {badgeProgress.achievements.length > 0 ? (
              badgeProgress.achievements.map((achievement, idx) => (
                <div key={achievement.id || idx} className="AutoboyExpress-premium-achievement-card earned">
                  <span style={{ fontSize: '2rem' }}>{achievement.icon || '🏆'}</span>
                  <h4>{achievement.name}</h4>
                  <p>{achievement.description}</p>
                  <span className="AutoboyExpress-premium-earned-badge">
                    Earned {achievement.earnedAt ? new Date(achievement.earnedAt).toLocaleDateString() : ''}
                  </span>
                </div>
              ))
            ) : (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', opacity: 0.6 }}>
                <FontAwesomeIcon icon={faAward} size="3x" style={{ marginBottom: '1rem' }} />
                <p>No achievements yet. Keep selling to earn badges!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderVIPSupportContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-premium-vip-header">
        <div className="AutoboyExpress-premium-vip-badge">
          <FontAwesomeIcon icon={faHeadset} />
        </div>
        <h2>VIP Support</h2>
        <p>Get priority assistance from our dedicated support team</p>
      </div>

      <div className="AutoboyExpress-dash-content">
        {/* Quick Contact */}
        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Quick Contact</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-premium-contact-options">
              <button className="AutoboyExpress-premium-contact-btn" onClick={handleVipCallSupport}>
                <FontAwesomeIcon icon={faPhone} />
                <div>
                  <h4>Call Us</h4>
                  <p>+234 800 123 4567</p>
                </div>
              </button>
              <button className="AutoboyExpress-premium-contact-btn" onClick={handleVipEmailSupport}>
                <FontAwesomeIcon icon={faEnvelope} />
                <div>
                  <h4>Email Support</h4>
                  <p>vip@AutoboyExpress.com</p>
                </div>
              </button>
              <button className="AutoboyExpress-premium-contact-btn" onClick={handleVipLiveChat}>
                <FontAwesomeIcon icon={faComments} />
                <div>
                  <h4>Live Chat</h4>
                  <p>Available 24/7</p>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Submit Ticket */}
        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Submit a Ticket</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <form className="AutoboyExpress-dash-settings-form" onSubmit={handleVipTicketSubmit}>
              <div className="AutoboyExpress-dash-form-group">
                <label>Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={vipTicketForm.subject}
                  onChange={handleVipTicketFormChange}
                  placeholder="Brief description of your issue"
                  required
                />
              </div>
              <div className="AutoboyExpress-dash-form-group">
                <label>Priority</label>
                <select
                  name="priority"
                  value={vipTicketForm.priority}
                  onChange={handleVipTicketFormChange}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="AutoboyExpress-dash-form-group">
                <label>Message</label>
                <textarea
                  name="message"
                  rows="6"
                  value={vipTicketForm.message}
                  onChange={handleVipTicketFormChange}
                  placeholder="Describe your issue in detail..."
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                disabled={isSubmittingVipTicket}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                {isSubmittingVipTicket ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  // Enhanced Wallet Content (same UI as SellerDashboard)
  const renderWalletContent = () => {
    const totalBalance = walletBalance.balance + walletBalance.heldBalance;

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div className="AutoboyExpress-dash-content">
          {/* Enhanced Wallet Header - Redesigned for Desktop */}
          <div className="AutoboyExpress-wallet-header-new">
            <div className="AutoboyExpress-wallet-header-left">
              <div className="AutoboyExpress-wallet-title-section">
                <h1 className="AutoboyExpress-wallet-main-title">
                  <FontAwesomeIcon icon={faWallet} className="AutoboyExpress-wallet-title-icon" />
                  My Wallet
                </h1>
                <p className="AutoboyExpress-wallet-description">Manage your earnings and withdrawals</p>
              </div>
            </div>
            <div className="AutoboyExpress-wallet-header-right">
              <div className="AutoboyExpress-wallet-total-card">
                <div className="AutoboyExpress-wallet-total-content">
                  <span className="AutoboyExpress-wallet-total-label">Total Balance</span>
                  <span className="AutoboyExpress-wallet-total-amount">{formatCurrency(totalBalance)}</span>
                  <div className="AutoboyExpress-wallet-total-badges">
                    <span className="AutoboyExpress-wallet-badge available">
                      <FontAwesomeIcon icon={faCheckCircle} /> Available: {formatCurrency(walletBalance.balance)}
                    </span>
                    <span className="AutoboyExpress-wallet-badge held">
                      <FontAwesomeIcon icon={faClipboardList} /> Held: {formatCurrency(walletBalance.heldBalance)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Balance Cards */}
          <div className="AutoboyExpress-wallet-cards-grid">
            {/* Available Balance Card */}
            <div className="AutoboyExpress-wallet-balance-card AutoboyExpress-wallet-available">
              <div className="AutoboyExpress-wallet-card-icon">
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <div className="AutoboyExpress-wallet-card-content">
                <span className="AutoboyExpress-wallet-card-label">Available Balance</span>
                <h2 className="AutoboyExpress-wallet-card-amount">{formatCurrency(walletBalance.balance)}</h2>
                <span className="AutoboyExpress-wallet-card-subtitle">Ready to withdraw</span>
              </div>
              <div className="AutoboyExpress-wallet-card-decoration"></div>
            </div>

            {/* Held Balance Card */}
            <div className="AutoboyExpress-wallet-balance-card AutoboyExpress-wallet-held">
              <div className="AutoboyExpress-wallet-card-icon">
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
              <div className="AutoboyExpress-wallet-card-content">
                <span className="AutoboyExpress-wallet-card-label">Held Balance</span>
                <h2 className="AutoboyExpress-wallet-card-amount">{formatCurrency(walletBalance.heldBalance)}</h2>
                <span className="AutoboyExpress-wallet-card-subtitle">Pending withdrawals</span>
              </div>
              <div className="AutoboyExpress-wallet-card-decoration"></div>
            </div>

            {/* Quick Actions Card */}
            <div className="AutoboyExpress-wallet-actions-card">
              <h3 className="AutoboyExpress-wallet-actions-title">Quick Actions</h3>
              <div className="AutoboyExpress-wallet-actions-buttons">
                <button
                  className="AutoboyExpress-wallet-action-btn AutoboyExpress-wallet-topup-btn"
                  onClick={() => setShowTopupModal(true)}
                  style={{ background: '#22C55E', color: '#fff' }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Top Up</span>
                </button>
                <button
                  className="AutoboyExpress-wallet-action-btn AutoboyExpress-wallet-withdraw-btn"
                  onClick={() => setShowWithdrawModal(true)}
                  disabled={walletBalance.balance < 1000}
                >
                  <FontAwesomeIcon icon={faDollarSign} />
                  <span>Withdraw</span>
                </button>
                <button
                  className="AutoboyExpress-wallet-action-btn AutoboyExpress-wallet-history-btn"
                  onClick={() => setShowBankAccounts(true)}
                  style={{ background: '#3B82F6', color: '#fff' }}
                >
                  <FontAwesomeIcon icon={faUniversity} />
                  <span>Bank Accounts</span>
                </button>
              </div>
              {walletBalance.balance < 1000 && (
                <div className="AutoboyExpress-wallet-minimum-notice">
                  <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '0.5rem' }} />
                  Minimum withdrawal: ₦1,000
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Transaction History */}
          <div className="AutoboyExpress-wallet-transactions">
            <div className="AutoboyExpress-wallet-transactions-header">
              <div>
                <h2 className="AutoboyExpress-wallet-transactions-title">
                  <FontAwesomeIcon icon={faHistory} style={{ marginRight: '0.75rem' }} />
                  Transaction History
                </h2>
                <p className="AutoboyExpress-wallet-transactions-subtitle">Recent wallet activity</p>
              </div>
              <div className="AutoboyExpress-wallet-transactions-actions">
                <button className="AutoboyExpress-wallet-filter-btn">
                  <FontAwesomeIcon icon={faFilter} />
                  Filter
                </button>
              </div>
            </div>

            <div className="AutoboyExpress-wallet-transactions-content">
              {isLoadingWallet ? (
                <div className="AutoboyExpress-wallet-empty-state">
                  <div className="AutoboyExpress-wallet-loading-spinner"></div>
                  <p>Loading transactions...</p>
                </div>
              ) : walletTransactions.length === 0 ? (
                <div className="AutoboyExpress-wallet-empty-state">
                  <div className="AutoboyExpress-wallet-empty-icon">
                    <FontAwesomeIcon icon={faWallet} />
                  </div>
                  <h3>No transactions yet</h3>
                  <p>Your transaction history will appear here</p>
                </div>
              ) : (
                <div className="AutoboyExpress-wallet-transactions-list">
                  {walletTransactions.map((transaction, index) => {
                    // Determine if transaction is a credit (adds money) or debit (removes money)
                    const isCreditTransaction =
                      transaction.type === 'credit' ||
                      transaction.type === 'topup' ||
                      transaction.type === 'refund' ||
                      transaction.type === 'escrow_release' ||
                      (transaction.description && transaction.description.toLowerCase().includes('escrow released')) ||
                      false;

                    const isDebitTransaction =
                      transaction.type === 'debit' ||
                      transaction.type === 'withdrawal' ||
                      transaction.type === 'payment' ||
                      transaction.type === 'purchase' ||
                      (transaction.description && (
                        transaction.description.toLowerCase().includes('payment') ||
                        transaction.description.toLowerCase().includes('purchase') ||
                        transaction.description.toLowerCase().includes('order')
                      ));

                    return (
                      <div key={transaction._id || transaction.id || index} className="AutoboyExpress-wallet-transaction-item">
                        <div className="AutoboyExpress-wallet-transaction-icon-wrapper">
                          <div className={`AutoboyExpress-wallet-transaction-icon ${isCreditTransaction ? 'credit' : 'debit'}`}>
                            <FontAwesomeIcon icon={
                              isCreditTransaction ? faCheckCircle :
                              isDebitTransaction ? faTimes :
                              faExchangeAlt
                            } />
                          </div>
                        </div>

                        <div className="AutoboyExpress-wallet-transaction-details">
                          <div className="AutoboyExpress-wallet-transaction-main">
                            <span className="AutoboyExpress-wallet-transaction-desc">
                              {transaction.description || transaction.type || 'Transaction'}
                            </span>
                            <span className={`AutoboyExpress-wallet-transaction-amount ${isCreditTransaction ? 'credit' : 'debit'}`}>
                              {isCreditTransaction ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                            </span>
                          </div>
                          <div className="AutoboyExpress-wallet-transaction-meta">
                            <span className="AutoboyExpress-wallet-transaction-date">
                              <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.375rem', fontSize: '0.75rem' }} />
                              {formatDate(transaction.created_at || transaction.createdAt, 'MMM d, yyyy - h:mm a')}
                            </span>
                            <span className={`AutoboyExpress-wallet-transaction-status ${
                              transaction.status === 'completed' || transaction.status === 'success' ? 'completed' :
                              transaction.status === 'failed' ? 'failed' : 'pending'
                            }`}>
                              {transaction.status === 'completed' || transaction.status === 'success' ? 'Completed' :
                               transaction.status === 'failed' ? 'Failed' : 'Pending'}
                            </span>
                          </div>
                        </div>

                        <div className={`AutoboyExpress-wallet-transaction-type-badge ${transaction.type}`}>
                          {transaction.type === 'escrow_release' ? 'Escrow' :
                           transaction.type === 'credit' || transaction.type === 'topup' || transaction.type === 'refund' ? 'Credit' :
                           'Debit'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Withdrawal Modal */}
        {showWithdrawModal && (
          <div className="AutoboyExpress-dash-modal-overlay" onClick={() => setShowWithdrawModal(false)}>
            <div className="AutoboyExpress-dash-modal" onClick={(e) => e.stopPropagation()}>
              <div className="AutoboyExpress-dash-modal-header">
                <h2>Request Withdrawal</h2>
                <button className="AutoboyExpress-dash-modal-close" onClick={() => setShowWithdrawModal(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <form onSubmit={handleWithdrawalSubmit}>
                <div className="AutoboyExpress-dash-modal-body">
                  <p style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
                    Available Balance: <strong>{formatCurrency(walletBalance.balance)}</strong>
                  </p>

                  <div className="AutoboyExpress-dash-form-group">
                    <label htmlFor="amount">Withdrawal Amount (₦) *</label>
                    <input
                      type="number"
                      id="amount"
                      name="amount"
                      value={withdrawalForm.amount}
                      onChange={handleWithdrawalFormChange}
                      min="1000"
                      max={walletBalance.balance}
                      required
                      placeholder="Minimum ₦1,000"
                    />
                    <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                      Minimum withdrawal amount is ₦1,000
                    </p>
                  </div>

                  {/* Saved Bank Accounts Section */}
                  {userBankAccounts.length > 0 && (
                    <div className="AutoboyExpress-dash-form-group">
                      <label htmlFor="savedBankAccount">Select Saved Bank Account</label>
                      <select
                        id="savedBankAccount"
                        value={withdrawalForm.selectedBankId}
                        onChange={handleBankAccountSelect}
                        style={{
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          fontSize: '1rem',
                          width: '100%'
                        }}
                      >
                        <option value="">-- Select a bank account or enter manually --</option>
                        {userBankAccounts.map((account) => (
                          <option key={account.id || account._id} value={account.id || account._id}>
                            {account.bank_name} - {account.account_number} {account.is_verified ? '✓ Verified' : ''}
                          </option>
                        ))}
                      </select>
                      <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                        {userBankAccounts.filter(acc => acc.is_verified).length > 0 ? (
                          <>✓ {userBankAccounts.filter(acc => acc.is_verified).length} verified account(s) available</>
                        ) : (
                          <>⚠️ No verified accounts. Please add and verify a bank account first.</>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Manual Entry Fields */}
                  <div className="AutoboyExpress-dash-form-group">
                    <label htmlFor="bankName">
                      Bank Name *
                      {withdrawalForm.selectedBankId && (
                        <span style={{ color: '#22C55E', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                          <FontAwesomeIcon icon={faCheckCircle} /> From saved account
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      id="bankName"
                      name="bankName"
                      value={withdrawalForm.bankName}
                      onChange={handleWithdrawalFormChange}
                      required
                      placeholder="e.g., GTBank"
                      readOnly={!!withdrawalForm.selectedBankId}
                      style={{
                        background: withdrawalForm.selectedBankId ? '#F0FDF4' : 'white',
                        cursor: withdrawalForm.selectedBankId ? 'not-allowed' : 'text'
                      }}
                    />
                  </div>

                  <div className="AutoboyExpress-dash-form-group">
                    <label htmlFor="accountName">
                      Account Name *
                      {withdrawalForm.selectedBankId && (
                        <span style={{ color: '#22C55E', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                          <FontAwesomeIcon icon={faCheckCircle} /> Verified
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      id="accountName"
                      name="accountName"
                      value={withdrawalForm.accountName}
                      onChange={handleWithdrawalFormChange}
                      required
                      placeholder="Account holder name"
                      readOnly={!!withdrawalForm.selectedBankId}
                      style={{
                        background: withdrawalForm.selectedBankId ? '#F0FDF4' : 'white',
                        cursor: withdrawalForm.selectedBankId ? 'not-allowed' : 'text'
                      }}
                    />
                  </div>

                  <div className="AutoboyExpress-dash-form-group">
                    <label htmlFor="bankAccount">
                      Account Number *
                      {withdrawalForm.selectedBankId && (
                        <span style={{ color: '#22C55E', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                          <FontAwesomeIcon icon={faCheckCircle} /> Verified
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      id="bankAccount"
                      name="bankAccount"
                      value={withdrawalForm.bankAccount}
                      onChange={handleWithdrawalFormChange}
                      required
                      pattern="[0-9]{10}"
                      placeholder="10-digit account number"
                      readOnly={!!withdrawalForm.selectedBankId}
                      style={{
                        background: withdrawalForm.selectedBankId ? '#F0FDF4' : 'white',
                        cursor: withdrawalForm.selectedBankId ? 'not-allowed' : 'text'
                      }}
                    />
                  </div>

                  {/* Add/Manage Bank Accounts Button */}
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: '#F3F4F6',
                    borderRadius: '8px',
                    border: '1px dashed #D1D5DB'
                  }}>
                    <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem' }}>
                      <FontAwesomeIcon icon={faUniversity} style={{ marginRight: '0.5rem' }} />
                      {userBankAccounts.length === 0 ? (
                        <>No saved bank accounts yet. Add a verified account for faster withdrawals.</>
                      ) : (
                        <>Manage your saved bank accounts or add a new verified account.</>
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowWithdrawModal(false);
                        setShowBankAccounts(true);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#3B82F6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      {userBankAccounts.length === 0 ? 'Add Bank Account' : 'Manage Bank Accounts'}
                    </button>
                  </div>
                </div>

                <div className="AutoboyExpress-dash-modal-footer">
                  <button
                    type="button"
                    className="AutoboyExpress-dash-secondary-button"
                    onClick={() => setShowWithdrawModal(false)}
                    disabled={isProcessingWithdrawal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="AutoboyExpress-dash-primary-button"
                    disabled={isProcessingWithdrawal}
                  >
                    {isProcessingWithdrawal ? 'Processing...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Top-up Modal */}
        {showTopupModal && (
          <div className="AutoboyExpress-dash-modal-overlay" onClick={() => setShowTopupModal(false)}>
            <div className="AutoboyExpress-dash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
              <div className="AutoboyExpress-dash-modal-header">
                <h2>Top Up Wallet</h2>
                <button
                  className="AutoboyExpress-dash-modal-close"
                  onClick={() => setShowTopupModal(false)}
                  disabled={isProcessingTopup}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="AutoboyExpress-dash-modal-body">
                <p style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
                  Enter the amount you want to add to your wallet. Minimum amount is ₦100.
                </p>
                <div className="AutoboyExpress-dash-form-group">
                  <label>Amount (NGN)</label>
                  <input
                    type="number"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    placeholder="Enter amount"
                    min="100"
                    step="100"
                    disabled={isProcessingTopup}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>
              <div className="AutoboyExpress-dash-modal-footer">
                <button
                  className="AutoboyExpress-dash-secondary-button"
                  onClick={() => setShowTopupModal(false)}
                  disabled={isProcessingTopup}
                >
                  Cancel
                </button>
                <button
                  className="AutoboyExpress-dash-primary-button"
                  onClick={handleWalletTopup}
                  disabled={isProcessingTopup || !topupAmount}
                >
                  {isProcessingTopup ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span style={{ marginLeft: '0.5rem' }}>Processing...</span>
                    </>
                  ) : (
                    `Top Up ₦${topupAmount || '0'}`
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bank Accounts Manager */}
        {showBankAccounts && (
          <BankAccountsManager
            onClose={() => {
              setShowBankAccounts(false);
              // Refresh bank accounts list after managing accounts
              fetchUserBankAccounts();
            }}
          />
        )}
      </div>
    );
  };

  const renderSwapDealsContent = () => {
    const STATUS_LABEL = {
      pending_seller_review:    { text: 'Awaiting Your Response', color: '#22C55E' },
      declined_by_seller:       { text: 'You Declined', color: '#ef4444' },
      pending_initiator_review: { text: 'Buyer Reviewing Price', color: '#6b7280' },
      declined_by_initiator:    { text: 'Buyer Declined', color: '#ef4444' },
      accepted_pending_payment: { text: 'Awaiting Payment', color: '#22C55E' },
      paid_in_escrow:           { text: 'In Escrow', color: '#16A34A' },
      shipped:                  { text: 'Shipped', color: '#0ea5e9' },
      delivered:                { text: 'Delivered', color: '#22c55e' },
      completed:                { text: 'Completed', color: '#22c55e' },
      cancelled:                { text: 'Cancelled', color: '#6b7280' },
    };
    const incomingOffers = getReceivedSwaps();
    const pendingCount = incomingOffers.filter(s => s.status === 'pending_seller_review').length;

    const handleRespond = async (swapId) => {
      const amount = parseFloat(swapTopupAmounts[swapId] || 0);
      if (isNaN(amount) || amount < 0) { showCustomToast({ type: 'warning', message: 'Enter a valid top-up amount (0 for a free swap)' }); return; }
      setSwapActionLoading(swapId + '_respond');
      try {
        await respondToOffer(swapId, amount);
        // Accepting commits the seller's own device to the swap — prompt for
        // that device's IMEI immediately.
        setSwapIMEITarget(swapId);
      } finally { setSwapActionLoading(null); }
    };
    const handleDecline = async (swapId) => {
      if (!window.confirm('Decline this swap offer?')) return;
      setSwapActionLoading(swapId + '_decline');
      try { await declineOffer(swapId); } finally { setSwapActionLoading(null); }
    };

    const handleConfirmShipment = async (swapId) => {
      if (!window.confirm('Confirm that you have shipped your device to the buyer?')) return;
      setSwapActionLoading(swapId + '_ship');
      try { await confirmShipment(swapId, { tracking_number: 'N/A' }); } finally { setSwapActionLoading(null); }
    };

    const handleConfirmDelivery = async (swapId) => {
      if (!window.confirm("Confirm that you have received the buyer's device?")) return;
      setSwapActionLoading(swapId + '_deliver');
      try { await confirmDelivery(swapId, false); } finally { setSwapActionLoading(null); }
    };

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { id: 'p2p',      label: 'P2P Swap Deals' },
            { id: 'requests', label: `Swap Requests${pendingCount ? ` (${pendingCount} new)` : incomingOffers.length ? ` (${incomingOffers.length})` : ''}` },
          ].map(tab => (
            <button key={tab.id} onClick={() => setSwapRequestsTab(tab.id)} style={{
              padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '13px',
              background: swapRequestsTab === tab.id ? '#22C55E' : (isDarkMode ? '#1e293b' : '#f1f5f9'),
              color: swapRequestsTab === tab.id ? '#fff' : (isDarkMode ? '#94a3b8' : '#475569'), transition: 'all 0.2s', position: 'relative',
            }}>
              {tab.id === 'requests' && pendingCount > 0 && (
                <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: '#fff', borderRadius: '50%', width: '18px', height: '18px', fontSize: '10px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{pendingCount}</span>
              )}
              {tab.label}
            </button>
          ))}
        </div>

        {swapRequestsTab === 'p2p' && <SwapDeals embedded isDarkMode={isDarkMode} />}

        {swapRequestsTab === 'requests' && (
          <div>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>Incoming Swap Requests</h3>

            {swapLoading && (
              <div style={{ textAlign: 'center', padding: '40px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px' }} />
              </div>
            )}

            {!swapLoading && incomingOffers.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 24px', borderRadius: '12px', background: isDarkMode ? '#1e293b' : '#f8fafc', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                <FontAwesomeIcon icon={faExchangeAlt} style={{ fontSize: '36px', marginBottom: '12px', opacity: 0.4 }} />
                <p style={{ margin: '0 0 4px', fontWeight: 600 }}>No swap requests yet</p>
                <p style={{ margin: 0, fontSize: '13px' }}>Buyers will send device swap offers for your listed products.</p>
              </div>
            )}

            {!swapLoading && incomingOffers.map(swap => {
              const statusInfo = STATUS_LABEL[swap.status] || { text: swap.status, color: '#6b7280' };
              const swapId = swap.id || swap._id;
              const isPending = swap.status === 'pending_seller_review';
              const listingProduct = swap.listing_product || swap.product || null;
              return (
                <div key={swapId} style={{
                  borderRadius: '12px', padding: '16px 20px', marginBottom: '12px',
                  background: isDarkMode ? '#1e293b' : '#fff',
                  border: `1px solid ${isPending ? '#22C55E' : (isDarkMode ? '#334155' : '#e2e8f0')}`,
                  boxShadow: isPending ? '0 0 0 2px rgba(34,197,94,0.15)' : (isDarkMode ? 'none' : '0 1px 4px rgba(0,0,0,0.06)'),
                }}>

                  {/* Header: offer number + status badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        #{swap.swap_number || String(swapId).slice(-8).toUpperCase()}
                      </span>
                      <h4 style={{ margin: '4px 0 2px', fontSize: '15px', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                        {swap.model}{swap.storage ? ` · ${swap.storage}` : ''}
                      </h4>
                      <span style={{ fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                        {swap.device_type} · Condition {swap.condition_rating}/10
                        {swap.battery_health != null ? ` · Battery ${swap.battery_health}%` : ''}
                        {swap.device_type === 'Laptop' && swap.ram ? ` · RAM ${swap.ram}` : ''}
                        {swap.device_type === 'Laptop' && swap.cpu ? ` · ${swap.cpu}` : ''}
                        {swap.repair_history ? ` · Repairs: ${swap.repair_history}` : ''}
                      </span>
                      <span style={{ display: 'block', fontSize: '11px', color: isDarkMode ? '#64748b' : '#94a3b8', marginTop: '2px' }}>
                        {new Date(swap.created_at || swap.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: statusInfo.color + '22', color: statusInfo.color, flexShrink: 0 }}>
                      {statusInfo.text}
                    </span>
                  </div>

                  {/* What the buyer wants (listing product) */}
                  {listingProduct && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', padding: '8px 10px', borderRadius: '8px', background: isDarkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${isDarkMode ? '#1e293b' : '#e2e8f0'}` }}>
                      {(listingProduct.images?.[0]?.url || listingProduct.images?.[0] || listingProduct.image) && (
                        <img src={listingProduct.images?.[0]?.url || listingProduct.images?.[0] || listingProduct.image} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                      )}
                      <div>
                        <div style={{ fontSize: '11px', color: isDarkMode ? '#64748b' : '#94a3b8' }}>Buyer wants your listing</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>{listingProduct.title || listingProduct.name}</div>
                        {listingProduct.price && <div style={{ fontSize: '12px', color: '#22c55e', fontWeight: 700 }}>₦{Number(listingProduct.price).toLocaleString()}</div>}
                      </div>
                    </div>
                  )}

                  {/* Device images */}
                  {swap.images?.length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                      {swap.images.slice(0, 5).map((img, i) => (
                        <img key={i} src={img} alt={`Device ${i + 1}`} style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }} onError={e => { e.target.style.display = 'none'; }} />
                      ))}
                      {swap.images.length > 5 && (
                        <div style={{ width: '52px', height: '52px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? '#0f172a' : '#f1f5f9', fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b' }}>+{swap.images.length - 5}</div>
                      )}
                    </div>
                  )}

                  {/* Action block: pending_seller_review — set price or decline */}
                  {isPending && (
                    <div style={{ marginTop: '14px', padding: '12px 14px', borderRadius: '8px', background: isDarkMode ? '#052e16' : '#f0fdf4', border: `1px solid ${isDarkMode ? '#166534' : '#bbf7d0'}` }}>
                      <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#4ade80' : '#166534' }}>Set top-up price (₦) — enter 0 for an equal swap</p>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <input
                          type="number" min="0" placeholder="e.g. 50000"
                          value={swapTopupAmounts[swapId] || ''}
                          onChange={e => setSwapTopupAmounts(prev => ({ ...prev, [swapId]: e.target.value }))}
                          style={{ flex: 1, minWidth: '120px', padding: '8px 12px', borderRadius: '7px', fontSize: '14px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#1e293b' : '#fff', color: isDarkMode ? '#f1f5f9' : '#1e293b' }}
                        />
                        <button disabled={swapActionLoading === swapId + '_respond'} onClick={() => handleRespond(swapId)} style={{ padding: '8px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#10b981', color: '#fff', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {swapActionLoading === swapId + '_respond' ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />} Send Price
                        </button>
                        <button disabled={swapActionLoading === swapId + '_decline'} onClick={() => handleDecline(swapId)} style={{ padding: '8px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#ef4444', color: '#fff', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {swapActionLoading === swapId + '_decline' ? <FontAwesomeIcon icon={faSpinner} spin /> : null} Decline
                        </button>
                      </div>

                      {/* Live fee breakdown (seller-only per API docs) */}
                      {swapTopupAmounts[swapId] && parseFloat(swapTopupAmounts[swapId]) > 0 && (() => {
                        try {
                          const feeData = calculateSwapFees(parseFloat(swapTopupAmounts[swapId]));
                          return (
                            <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '6px', background: isDarkMode ? '#052e16' : '#f0fdf4', border: `1px solid ${isDarkMode ? '#166534' : '#bbf7d0'}`, fontSize: '12px', color: isDarkMode ? '#f1f5f9' : '#166534' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div><span style={{ opacity: 0.8 }}>Top-up Amount:</span><div style={{ fontWeight: 600, fontSize: '13px' }}>₦{parseFloat(swapTopupAmounts[swapId]).toLocaleString()}</div></div>
                                <div><span style={{ opacity: 0.8 }}>Platform Fee ({feeData.platformFeePercentage}%):</span><div style={{ fontWeight: 600, fontSize: '13px' }}>₦{feeData.platformFee.toLocaleString()}</div></div>
                                <div><span style={{ opacity: 0.8 }}>VAT ({feeData.vatPercentage}%):</span><div style={{ fontWeight: 600, fontSize: '13px' }}>₦{feeData.vat.toLocaleString()}</div></div>
                                <div><span style={{ opacity: 0.8 }}>Buyer Pays Total:</span><div style={{ fontWeight: 700, fontSize: '13px', color: isDarkMode ? '#4ade80' : '#16A34A' }}>₦{feeData.total.toLocaleString()}</div></div>
                              </div>
                            </div>
                          );
                        } catch { return null; }
                      })()}
                    </div>
                  )}

                  {/* IMEI for the seller's OWN device — required once accepted.
                      Stays available until submitted, in case the post-accept
                      prompt was dismissed. */}
                  {!isPending && !swap.seller_imei_submitted && !swapIMEISubmittedIds.includes(swapId)
                    && !['declined_by_seller', 'declined_by_initiator', 'cancelled'].includes(swap.status) && (
                    <div style={{ marginTop: '12px' }}>
                      <button onClick={() => setSwapIMEITarget(swapId)}
                        style={{ width: '100%', padding: '9px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#f59e0b', color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                        <FontAwesomeIcon icon={faMobileAlt} /> Enter IMEI for Your Device
                      </button>
                    </div>
                  )}

                  {/* Post-payment fulfillment actions (seller side) */}
                  {swap.status === 'paid_in_escrow' && (
                    <div style={{ marginTop: '12px' }}>
                      <button
                        disabled={swapActionLoading === swapId + '_ship'}
                        onClick={() => handleConfirmShipment(swapId)}
                        style={{ width: '100%', padding: '9px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#0ea5e9', color: '#fff', fontWeight: 600, fontSize: '13px' }}
                      >
                        {swapActionLoading === swapId + '_ship' ? <FontAwesomeIcon icon={faSpinner} spin /> : null} Mark my Device as Shipped
                      </button>
                    </div>
                  )}

                  {swap.status === 'shipped' && (
                    <div style={{ marginTop: '12px' }}>
                      <button
                        disabled={swapActionLoading === swapId + '_deliver'}
                        onClick={() => handleConfirmDelivery(swapId)}
                        style={{ width: '100%', padding: '9px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#22c55e', color: '#fff', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}
                      >
                        {swapActionLoading === swapId + '_deliver' ? <FontAwesomeIcon icon={faSpinner} spin /> : '✅'} Confirm Buyer's Device Received
                      </button>
                    </div>
                  )}

                  {/* Status info for responded/later stages */}
                  {!isPending && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                      {swap.status === 'pending_initiator_review' && swap.seller_topup_amount != null && (
                        <span>📨 Price sent to buyer — ₦{Number(swap.total_payable || swap.seller_topup_amount).toLocaleString()} total. Awaiting buyer decision.</span>
                      )}
                      {swap.status === 'pending_initiator_review' && swap.seller_topup_amount == null && (
                        <span>📨 Price offer sent. Awaiting buyer decision.</span>
                      )}
                      {swap.status === 'declined_by_initiator' && '❌ Buyer declined your price. The offer is closed.'}
                      {swap.status === 'declined_by_seller' && '✗ You declined this offer.'}
                      {swap.status === 'accepted_pending_payment' && '💳 Buyer accepted your price — awaiting their payment.'}
                      {swap.status === 'paid_in_escrow' && '🔒 Payment in escrow — ship your device to the buyer to proceed.'}
                      {swap.status === 'shipped' && "🚚 Your device is shipped — confirm once you've received the buyer's device."}
                      {swap.status === 'delivered' && '📬 Both devices delivered — awaiting buyer to complete the swap.'}
                      {swap.status === 'completed' && '✅ Swap completed. Funds released to your wallet.'}
                      {swap.status === 'cancelled' && '🚫 This swap was cancelled.'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {swapIMEITarget && (
          <IMEISubmissionModal
            unitCount={1}
            title="IMEI — Your Swap Device"
            isDarkMode={isDarkMode}
            onSubmit={async (imeis) => {
              await swapAPI.submitSellerIMEI(swapIMEITarget, imeis[0].imei1, imeis[0].imei2);
              setSwapIMEISubmittedIds(prev => [...prev, swapIMEITarget]);
              setSwapIMEITarget(null);
              showCustomToast({ type: 'success', message: 'IMEI submitted successfully' });
            }}
            onClose={() => setSwapIMEITarget(null)}
          />
        )}
      </div>
    );
  };

  const renderEscrowContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faLock} /> Escrow Analytics & Settings
          </h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <EscrowAnalytics fetchOrders={() => sellerAPI.getOrders()} />
          <EscrowSettingsPanel />
          <div style={{ textAlign: 'center', padding: '3rem 1rem', marginTop: '2rem' }}>
            <h3 style={{
              fontSize: '1.5rem',
              marginBottom: '1rem',
              color: isDarkMode ? '#F9FAFB' : '#1F2937'
            }}>
              How Escrow Protection Works for Sellers
            </h3>
            <p style={{
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              marginBottom: '2rem',
              maxWidth: '600px',
              margin: '0 auto 2rem'
            }}>
              All your sales are protected with escrow. Funds are held securely until the buyer confirms delivery, ensuring safe transactions for both parties.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              maxWidth: '900px',
              margin: '0 auto',
              textAlign: 'left'
            }}>
              <div style={{
                background: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : '#F9FAFB',
                padding: '1.5rem',
                borderRadius: '12px',
                border: isDarkMode ? '2px solid rgba(60, 60, 60, 0.6)' : '2px solid #E5E7EB'
              }}>
                <div style={{
                  background: '#22C55E',
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1rem'
                }}>
                  <FontAwesomeIcon icon={faShoppingCart} style={{ color: 'white', fontSize: '1.25rem' }} />
                </div>
                <h4 style={{
                  fontSize: '1.1rem',
                  marginBottom: '0.5rem',
                  color: isDarkMode ? '#F9FAFB' : '#1F2937'
                }}>
                  1. Customer Orders
                </h4>
                <p style={{
                  color: isDarkMode ? '#9CA3AF' : '#6B7280',
                  fontSize: '0.9rem',
                  lineHeight: '1.6'
                }}>
                  Payment is held in secure escrow when a customer places an order for your product.
                </p>
              </div>
              <div style={{
                background: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : '#F9FAFB',
                padding: '1.5rem',
                borderRadius: '12px',
                border: isDarkMode ? '2px solid rgba(60, 60, 60, 0.6)' : '2px solid #E5E7EB'
              }}>
                <div style={{
                  background: '#22C55E',
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1rem'
                }}>
                  <FontAwesomeIcon icon={faBox} style={{ color: 'white', fontSize: '1.25rem' }} />
                </div>
                <h4 style={{
                  fontSize: '1.1rem',
                  marginBottom: '0.5rem',
                  color: isDarkMode ? '#F9FAFB' : '#1F2937'
                }}>
                  2. Ship Your Product
                </h4>
                <p style={{
                  color: isDarkMode ? '#9CA3AF' : '#6B7280',
                  fontSize: '0.9rem',
                  lineHeight: '1.6'
                }}>
                  Process and ship the order to the customer. The payment remains in escrow during delivery.
                </p>
              </div>
              <div style={{
                background: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : '#F9FAFB',
                padding: '1.5rem',
                borderRadius: '12px',
                border: isDarkMode ? '2px solid rgba(60, 60, 60, 0.6)' : '2px solid #E5E7EB'
              }}>
                <div style={{
                  background: '#16A34A',
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1rem'
                }}>
                  <FontAwesomeIcon icon={faDollarSign} style={{ color: 'white', fontSize: '1.25rem' }} />
                </div>
                <h4 style={{
                  fontSize: '1.1rem',
                  marginBottom: '0.5rem',
                  color: isDarkMode ? '#F9FAFB' : '#1F2937'
                }}>
                  3. Get Paid
                </h4>
                <p style={{
                  color: isDarkMode ? '#9CA3AF' : '#6B7280',
                  fontSize: '0.9rem',
                  lineHeight: '1.6'
                }}>
                  Once the buyer confirms delivery, funds are released from escrow to your wallet automatically.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDisputesContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faExclamationTriangle} /> Disputes
          </h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <Disputes />
        </div>
      </div>
    </div>
  );

  const getContentForSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboardContent();
      case 'kyc':
        return (
          <div className="AutoboyExpress-dash-content-wrapper">
            <div className="AutoboyExpress-dash-content">
              <KYCVerification
                userType="seller"
                email={userProfile?.email || ''}
                onComplete={() => {
                  showCustomToast({
                    type: 'success',
                    title: 'KYC Submitted',
                    message: 'KYC verification submitted successfully! We will review your documents.'
                  });
                  setTimeout(() => setActiveSection('dashboard'), 2000);
                }}
              />
            </div>
          </div>
        );
      case 'products':
        return renderProductsContent();
      case 'orders':
        return renderOrdersContent();
      case 'earnings':
        return renderEarningsContent();
      case 'wallet':
        return renderWalletContent();
      case 'addresses':
        return (
          <AddressManagement
            onToast={showCustomToast}
            isDarkMode={isDarkMode}
          />
        );
      case 'swap-deals':
        return renderSwapDealsContent();
      case 'escrow':
        return renderEscrowContent();
      case 'analytics':
        return renderAnalyticsContent();
      case 'reports':
        return renderReportsContent();
      case 'badge':
        return renderBadgeContent();
      case 'vip-support':
        return renderVIPSupportContent();
      case 'disputes':
        return renderDisputesContent();
      case 'settings':
        return renderSettingsContent();
      default:
        return renderDashboardContent();
    }
  };

  if (loading) {
    return (
      <>
        <div className="AutoboyExpress-dash-container" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: isDarkMode ? '#0F172A' : '#F9FAFB'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="AutoboyExpress-dash-loading-spinner"></div>
            <p style={{ marginTop: '1rem', color: '#22C55E', fontWeight: '600' }}>Loading dashboard...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {showAddProductForm && renderAddProductForm()}
      <div className="AutoboyExpress-dash-container">
      {/* Sidebar */}
      <div className={`AutoboyExpress-dash-sidebar ${isMobileSidebarOpen ? 'AutoboyExpress-dash-sidebar-open' : ''}`}>
        <div className="AutoboyExpress-dash-logo">
          <img
            src={isDarkMode ? darkLogo : lightLogo}
            alt="AutoboyExpress"
          />
        </div>

        <div className="AutoboyExpress-dash-profile">
          <div className="AutoboyExpress-dash-profile-avatar" style={{ overflow: 'hidden', borderRadius: '50%', background: '#1f2937' }}>
            {(avatarPreview || userProfile?.avatar)
              ? <img src={avatarPreview || userProfile.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={e => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style="width:60%;height:60%;fill:#9ca3af;margin:20%"><path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z"/></svg>'; }} />
              : <FontAwesomeIcon icon={faUser} />}
          </div>
          <div className="AutoboyExpress-dash-profile-info">
            <h3 className="AutoboyExpress-dash-seller-name">{userProfile.name}</h3>
            <p className="AutoboyExpress-dash-seller-status AutoboyExpress-premium-badge-text">
              <FontAwesomeIcon icon={faCrown} /> Premium Seller
            </p>
          </div>
        </div>

        <ul className="AutoboyExpress-dash-nav">
          <li className="AutoboyExpress-dash-nav-item">
            <button
              className="AutoboyExpress-dash-nav-link"
              onClick={() => {
                navigate('/homepage');
                setIsMobileSidebarOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faHome} className="AutoboyExpress-dash-nav-icon" />
              Homepage
            </button>
          </li>
          {[
            { id: 'dashboard', icon: faChartBar, label: 'Dashboard' },
            { id: 'kyc', icon: faShieldAlt, label: 'KYC Verification' },
            { id: 'products', icon: faBox, label: 'Products' },
            { id: 'orders', icon: faShoppingCart, label: 'Orders' },
            { id: 'earnings', icon: faDollarSign, label: 'Earnings' },
            { id: 'wallet', icon: faWallet, label: 'Wallet' },
            { id: 'addresses', icon: faMapMarkerAlt, label: 'My Addresses' },
            { id: 'swap-deals', icon: faExchangeAlt, label: 'Swap Deals' },
            { id: 'escrow', icon: faLock, label: 'Escrow' },
            { id: 'analytics', icon: faChartLine, label: 'Analytics' },
            { id: 'reports', icon: faFileAlt, label: 'Reports', isPremium: true },
            { id: 'badge', icon: faCrown, label: 'Badge', isPremium: true },
            { id: 'vip-support', icon: faHeadset, label: 'VIP Support', isPremium: true },
            { id: 'disputes', icon: faExclamationTriangle, label: 'Disputes' },
            { id: 'settings', icon: faCog, label: 'Settings' }
          ].map(item => (
            <li key={item.id} className="AutoboyExpress-dash-nav-item">
              <button
                className={`AutoboyExpress-dash-nav-link ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveSection(item.id);
                  setIsMobileSidebarOpen(false);
                }}
              >
                <FontAwesomeIcon icon={item.icon} className="AutoboyExpress-dash-nav-icon" />
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="AutoboyExpress-dash-mobile-overlay"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="AutoboyExpress-dash-main">
        {/* Header */}
        <div className="AutoboyExpress-dash-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              className="AutoboyExpress-dash-mobile-toggle"
              onClick={toggleMobileSidebar}
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
            <h1 className="AutoboyExpress-dash-title">
              {activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}
            </h1>
          </div>
          <div className="AutoboyExpress-dash-header-actions">
            {/* Notifications */}
            <div className="AutoboyExpress-dash-notification-wrapper">
              <button
                className="AutoboyExpress-dash-notification-btn"
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  setShowSettings(false);
                }}
              >
                <FontAwesomeIcon icon={faBell} />
                {unreadCount > 0 && (
                  <span className="AutoboyExpress-dash-notification-badge">
                    {unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="AutoboyExpress-dash-dropdown AutoboyExpress-dash-notifications-dropdown">
                  <div className="AutoboyExpress-dash-dropdown-header">
                    <h3>Notifications</h3>
                    <button className="AutoboyExpress-dash-mark-read-btn">Mark all as read</button>
                  </div>
                  <div className="AutoboyExpress-dash-dropdown-content">
                    {notifications.length === 0 ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF' }}>
                        No notifications
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div
                          key={notif._id || notif.id}
                          className={`AutoboyExpress-dash-notification-item ${!notif.read && !notif.is_read ? 'unread' : ''}`}
                          onClick={() => handleMarkNotificationRead(notif._id || notif.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="AutoboyExpress-dash-notification-icon">
                            <FontAwesomeIcon icon={
                              notif.type === 'order_update' ? faShoppingCart :
                              notif.type === 'payment_update' ? faDollarSign :
                              notif.type === 'new_message' ? faComments :
                              notif.type === 'wallet_update' ? faWallet :
                              notif.type === 'product_update' ? faBox :
                              notif.type === 'price_alert' ? faBolt :
                              faBell
                            } />
                          </div>
                          <div className="AutoboyExpress-dash-notification-content">
                            <h4>{notif.title}</h4>
                            <p>{notif.message}</p>
                            <span>{notif.timestamp ? formatDate(notif.timestamp, 'MMM d, h:mm a') : notif.time}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="AutoboyExpress-dash-dropdown-footer">
                    <button onClick={() => { navigate('/notifications'); setShowNotifications(false); }}>View All Notifications</button>
                  </div>
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <button className="AutoboyExpress-dash-theme-toggle" onClick={toggleTheme}>
              <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} />
            </button>

            {/* Profile Picture */}
            <div
              className="AutoboyExpress-dash-profile-picture"
              onClick={() => setActiveSection('settings')}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '2px solid #22C55E',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
                marginLeft: '15px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <img
                src={avatarPreview || userProfile?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.firstName || 'S')}+${encodeURIComponent(userProfile?.lastName || 'eller')}&background=22C55E&color=fff&size=128`}
                alt="Profile"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.firstName || 'S')}+${encodeURIComponent(userProfile?.lastName || 'eller')}&background=22C55E&color=fff&size=128`;
                }}
              />
            </div>

            {/* Settings Dropdown */}
            <div className="AutoboyExpress-dash-settings-wrapper">
              <button
                className="AutoboyExpress-dash-settings-btn"
                onClick={() => {
                  setShowSettings(!showSettings);
                  setShowNotifications(false);
                }}
              >
                <FontAwesomeIcon icon={faCog} />
              </button>

              {showSettings && (
                <div className="AutoboyExpress-dash-dropdown AutoboyExpress-dash-settings-dropdown">
                  <div className="AutoboyExpress-dash-dropdown-content">
                    <button className="AutoboyExpress-dash-dropdown-item" onClick={() => setActiveSection('settings')}>
                      <FontAwesomeIcon icon={faUserCircle} />
                      <span>My Profile</span>
                    </button>
                    <button className="AutoboyExpress-dash-dropdown-item" onClick={() => setActiveSection('settings')}>
                      <FontAwesomeIcon icon={faCog} />
                      <span>Settings</span>
                    </button>
                    <button className="AutoboyExpress-dash-dropdown-item">
                      <FontAwesomeIcon icon={faShieldAlt} />
                      <span>Privacy & Security</span>
                    </button>
                    <button className="AutoboyExpress-dash-dropdown-item">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                      <span>Help & Support</span>
                    </button>
                    <button className="AutoboyExpress-dash-dropdown-item" onClick={() => setActiveSection('vip-support')}>
                      <FontAwesomeIcon icon={faHeadset} />
                      <span>VIP Support</span>
                    </button>
                    <div className="AutoboyExpress-dash-dropdown-divider"></div>
                    <button className="AutoboyExpress-dash-dropdown-item AutoboyExpress-dash-logout-btn" onClick={handleLogout}>
                      <FontAwesomeIcon icon={faSignOutAlt} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="AutoboyExpress-dash-content-area">
          {getContentForSection()}
        </div>
      </div>

      {/* View Product Modal */}
      {showViewProductModal && selectedProduct && (
        <div className="AutoboyExpress-dash-modal-overlay" onClick={() => setShowViewProductModal(false)}>
          <div className="AutoboyExpress-dash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="AutoboyExpress-dash-modal-header">
              <h2>Product Details</h2>
              <button className="AutoboyExpress-dash-modal-close" onClick={() => setShowViewProductModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="AutoboyExpress-dash-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Product Image */}
              <div style={{ marginBottom: '2rem' }}>
                <img
                  src={selectedProduct.image || 'https://via.placeholder.com/400x400?text=No+Image'}
                  alt={selectedProduct.name}
                  style={{ width: '100%', maxHeight: '400px', objectFit: 'cover', borderRadius: '8px' }}
                />
              </div>

              {/* Product Info Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div className="AutoboyExpress-dash-form-group">
                  <label><strong>Product Name</strong></label>
                  <p>{selectedProduct.name || selectedProduct.title}</p>
                </div>

                <div className="AutoboyExpress-dash-form-group">
                  <label><strong>Price</strong></label>
                  <p>{formatCurrency(selectedProduct.price)}</p>
                </div>

                <div className="AutoboyExpress-dash-form-group">
                  <label><strong>Stock</strong></label>
                  <p>{selectedProduct.quantity || selectedProduct.stock || 0}</p>
                </div>

                <div className="AutoboyExpress-dash-form-group">
                  <label><strong>Condition</strong></label>
                  <p style={{ textTransform: 'capitalize' }}>{selectedProduct.condition || 'N/A'}</p>
                </div>

                <div className="AutoboyExpress-dash-form-group">
                  <label><strong>Brand</strong></label>
                  <p>{selectedProduct.brand || 'N/A'}</p>
                </div>

                <div className="AutoboyExpress-dash-form-group">
                  <label><strong>Model</strong></label>
                  <p>{selectedProduct.model || 'N/A'}</p>
                </div>

                <div className="AutoboyExpress-dash-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label><strong>Description</strong></label>
                  <p>{selectedProduct.description || 'No description'}</p>
                </div>

                {selectedProduct.specifications && (
                  <div className="AutoboyExpress-dash-form-group" style={{ gridColumn: '1 / -1' }}>
                    <label><strong>Specifications</strong></label>
                    <p>{selectedProduct.specifications.details || selectedProduct.specifications}</p>
                  </div>
                )}

                <div className="AutoboyExpress-dash-form-group">
                  <label><strong>Swap Available</strong></label>
                  <p>{selectedProduct.swap_available ? 'Yes' : 'No'}</p>
                </div>

                <div className="AutoboyExpress-dash-form-group">
                  <label><strong>Status</strong></label>
                  <p style={{ textTransform: 'capitalize' }}>{selectedProduct.status || 'active'}</p>
                </div>
              </div>

              <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
                  onClick={() => {
                    setShowViewProductModal(false);
                    handleEditProduct(selectedProduct);
                  }}
                >
                  Edit Product
                </button>
                <button
                  className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                  onClick={() => setShowViewProductModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditProductModal && editProduct && (
        <div className="AutoboyExpress-dash-modal-overlay" onClick={() => setShowEditProductModal(false)}>
          <div className="AutoboyExpress-dash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="AutoboyExpress-dash-modal-header">
              <h2>Edit Product</h2>
              <button className="AutoboyExpress-dash-modal-close" onClick={() => setShowEditProductModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleUpdateProduct} className="AutoboyExpress-dash-product-form">
              {/* Same form as Add Product */}
              <div className="AutoboyExpress-dash-form-section">
                <h3>Product Images (Max 10)</h3>
                <div className="AutoboyExpress-dash-image-upload-area">
                  <input
                    type="file"
                    id="edit-product-images"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    disabled={uploadingImages || productImages.length >= 10}
                  />
                  <label
                    htmlFor="edit-product-images"
                    className="AutoboyExpress-dash-upload-label"
                    style={{
                      opacity: uploadingImages || productImages.length >= 10 ? 0.6 : 1,
                      cursor: uploadingImages || productImages.length >= 10 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <FontAwesomeIcon icon={uploadingImages ? faSpinner : faUpload} spin={uploadingImages} />
                    <span>
                      {uploadingImages
                        ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                        : productImages.length >= 10
                          ? 'Maximum images reached (10/10)'
                          : 'Click to upload images from your device'}
                    </span>
                    {uploadingImages && uploadProgress.fileName && (
                      <small style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                        {uploadProgress.fileName}
                      </small>
                    )}
                  </label>
                </div>

                <div className="AutoboyExpress-dash-form-group" style={{ marginTop: '1rem' }}>
                  <label>Or Add Image URL</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={imageUrlInput}
                      onChange={(e) => setImageUrlInput(e.target.value)}
                      className="AutoboyExpress-dash-input"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={handleAddImageUrl}
                      className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                      style={{ padding: '0.5rem 1rem' }}
                      disabled={productImages.length >= 10}
                    >
                      Add URL
                    </button>
                  </div>
                </div>

                {productImages.length > 0 && (
                  <div className="AutoboyExpress-dash-image-preview-grid">
                    {productImages.map((img, index) => (
                      <div key={index} className="AutoboyExpress-dash-image-preview">
                        <img src={img} alt={`Product ${index + 1}`} />
                        <button
                          type="button"
                          className="AutoboyExpress-dash-remove-image"
                          onClick={() => removeImage(index)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="AutoboyExpress-dash-form-row">
                <div className="AutoboyExpress-dash-form-group">
                  <label>Product Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={editProduct.title}
                    onChange={(e) => setEditProduct({ ...editProduct, title: e.target.value })}
                    placeholder="e.g., iPhone 15 Pro Max 256GB"
                    required
                  />
                </div>
              </div>

              <div className="AutoboyExpress-dash-form-row">
                <div className="AutoboyExpress-dash-form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={editProduct.category}
                    onChange={(e) => setEditProduct({ ...editProduct, category: e.target.value })}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.length > 0 ? (
                      categories.map(cat => (
                        <option key={cat._id || cat.id} value={cat._id || cat.id}>
                          {cat.name}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="smartphones">Smartphones</option>
                        <option value="laptops">Laptops</option>
                        <option value="tablets">Tablets</option>
                        <option value="smartwatches">Smartwatches</option>
                        <option value="headphones">Headphones</option>
                        <option value="gaming_consoles">Gaming Consoles</option>
                        <option value="cameras">Cameras</option>
                        <option value="audio_equipment">Audio Equipment</option>
                        <option value="accessories">Accessories</option>
                      </>
                    )}
                  </select>
                  {editProduct.category === 'accessories' && JSON.parse(localStorage.getItem('user') || '{}').user_type !== 'admin' && (
                    <div style={{
                      color: '#ef4444', fontSize: '0.85rem', marginTop: '4px',
                      padding: '8px 12px', background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.2)'
                    }}>
                      <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }} />
                      Only admins can post in the Accessories category. Please select a different category.
                    </div>
                  )}
                </div>

                <div className="AutoboyExpress-dash-form-group">
                  <label>Condition *</label>
                  <select
                    name="condition"
                    value={editProduct.condition}
                    onChange={(e) => setEditProduct({ ...editProduct, condition: e.target.value })}
                    required
                  >
                    <option value="new">Brand New</option>
                    <option value="uk_used">UK Used</option>
                    <option value="nigeria_used">Nigerian Used</option>
                    <option value="swap_deal">Swap Deal</option>
                    <option value="for_sale">For Sale</option>
                  </select>
                </div>
              </div>

              <div className="AutoboyExpress-dash-form-row">
                <div className="AutoboyExpress-dash-form-group">
                  <label>Brand</label>
                  <input
                    type="text"
                    name="brand"
                    value={editProduct.brand}
                    onChange={(e) => setEditProduct({ ...editProduct, brand: e.target.value })}
                    placeholder="e.g., Apple, Samsung"
                  />
                </div>

                <div className="AutoboyExpress-dash-form-group">
                  <label>Model</label>
                  <input
                    type="text"
                    name="model"
                    value={editProduct.model}
                    onChange={(e) => setEditProduct({ ...editProduct, model: e.target.value })}
                    placeholder="e.g., iPhone 15 Pro Max"
                  />
                </div>
              </div>

              <div className="AutoboyExpress-dash-form-row">
                <div className="AutoboyExpress-dash-form-group">
                  <label>Colours available</label>
                  <ColorMultiSelect
                    value={editProduct.colors || []}
                    onChange={(colors) => setEditProduct({ ...editProduct, colors, color: colors[0] || '' })}
                    availableColors={availableColors}
                    loading={loadingColors}
                    modelEntered={!!editProduct.model}
                    isDarkMode={isDarkMode}
                  />
                </div>
              </div>

              <div className="AutoboyExpress-dash-form-row">
                <div className="AutoboyExpress-dash-form-group">
                  <label>Price (₦) *</label>
                  <input
                    type="number"
                    name="price"
                    value={editProduct.price}
                    onChange={(e) => setEditProduct({ ...editProduct, price: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                    Regular price shown to buyers
                  </small>
                </div>

                <div className="AutoboyExpress-dash-form-group">
                  <label>Seller Price (₦) - Optional</label>
                  <input
                    type="number"
                    name="sellerPrice"
                    value={editProduct.sellerPrice}
                    onChange={(e) => setEditProduct({ ...editProduct, sellerPrice: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                    Wholesale price for other sellers (must be less than regular price)
                  </small>
                </div>
              </div>

              <div className="AutoboyExpress-dash-form-row">
                <div className="AutoboyExpress-dash-form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    name="quantity"
                    value={editProduct.quantity}
                    onChange={(e) => setEditProduct({ ...editProduct, quantity: e.target.value })}
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div className="AutoboyExpress-dash-form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={editProduct.description}
                  onChange={(e) => setEditProduct({ ...editProduct, description: e.target.value })}
                  placeholder="Detailed product description..."
                  rows="4"
                  required
                ></textarea>
              </div>

              <div className="AutoboyExpress-dash-form-group">
                <label>Specifications</label>
                <textarea
                  name="specifications"
                  value={editProduct.specifications}
                  onChange={(e) => setEditProduct({ ...editProduct, specifications: e.target.value })}
                  placeholder="Key specifications (optional)"
                  rows="3"
                ></textarea>
              </div>

              <div className="AutoboyExpress-dash-form-row">
                <div className="AutoboyExpress-dash-form-group">
                  <label>Location (City, State) *</label>
                  <input
                    type="text"
                    name="location"
                    value={editProduct.location}
                    onChange={(e) => setEditProduct({ ...editProduct, location: e.target.value })}
                    placeholder="e.g., Lagos, Lagos"
                    required
                  />
                </div>

                <div className="AutoboyExpress-dash-form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editProduct.swapAvailable}
                      onChange={(e) => setEditProduct({ ...editProduct, swapAvailable: e.target.checked })}
                    />
                    {' '}Available for Swap
                  </label>
                </div>
              </div>

              <div className="AutoboyExpress-dash-form-actions">
                <button
                  type="button"
                  className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
                  onClick={() => setShowEditProductModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary">
                  <FontAwesomeIcon icon={faSave} />
                  Update Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default PremiumSellerDashboard;

